"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  AlertTriangle,
  Archive,
  AtSign,
  Bot,
  Check,
  BookPlus,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  FileText,
  Filter,
  Forward,
  Inbox,
  ListChecks,
  Mail,
  MailOpen,
  MessageSquare,
  Paperclip,
  PenSquare,
  Download,
  ExternalLink,
  Link2,
  Loader2,
  MoreHorizontal,
  Printer,
  RefreshCw,
  Search,
  Reply,
  ReplyAll,
  Send,
  Sparkles,
  Star,
  Trash2,
  UserPlus,
  X,
  Settings,
  BookmarkPlus,
  ShieldAlert,
  Users,
  ClipboardCheck,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PORTAL_ROUTES } from "@/lib/routing/portal-routes";
import { parseSearch, SEARCH_OPERATORS } from "@/lib/inbox/search-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CATEGORY_LABELS,
  CURRENT_USER,
  MAILBOXES,
  STAFF,
  THREADS,
  type Category,
  type LinkedBooking,
  type Mailbox,
  type Message,
  type StaffMember,
  type SuggestedAction,
  type SuggestedDraft,
  type Thread,
} from "./mock-data";
import type {
  ConversationCursor,
  InboxCountRow,
} from "@/lib/inbox/queries";
import { EmailHtmlFrame } from "./email-html-frame";
import { LocalTime } from "./local-time";
import { formatFullDate } from "@/lib/inbox/format-time";
import { MailEditor, mailDraft, type MailDraft } from "./mail-editor";
import { byNewest, mergeRefreshedPage } from "./thread-list";
import { AddToFaqDialog } from "./add-to-faq-dialog";
import { AiFeedback } from "./ai-feedback";
import {
  AttachButton,
  AttachmentList,
  sendWithAttachments,
} from "./attachment-picker";
import { formatFileSize } from "@/lib/inbox/attachments";
import { MentionTextarea, mentionedIn, withMentions } from "./mention-textarea";
import {
  addInternalNote,
  askAssistant,
  clearMentionForMe,
  getConversationMessages,
  getMessageHtml,
  loadMoreConversations,
  requestInboxSync,
  searchInbox,
  markConversationRead,
  markConversationUnread,
  sendNewMail as sendNewMailAction,
  sendReply,
  decideSuggestedAction,
  prepareParticipantChange,
  getSuggestedDraft,
  prepareSuggestedAction,
  regenerateDraft,
  setConversationAssignee,
  setConversationDone,
  setConversationKind,
  setConversationStar,
  setConversationMarkedForReview,
  syncInboxNow,
} from "./actions";

/**
 * The mailboxes this inbox is showing.
 *
 * Supplied by InboxClient so the tree can render real connected mailboxes
 * instead of the wireframe's fixtures, without threading a prop through every
 * nested component. Falls back to the fixtures, which is what keeps the
 * booking portal's wireframe working untouched.
 */
const MailboxContext = createContext<Mailbox[]>(MAILBOXES);

const useMailboxes = (): Mailbox[] => useContext(MailboxContext);

/**
 * Who works the inbox, and who is looking at it. Real staff when the inbox is
 * live; the wireframe's fixtures otherwise. `live` also tells nested
 * components whether their buttons should reach the server.
 */
type InboxPeople = {
  staff: StaffMember[];
  me: StaffMember;
  live: boolean;
  /** My email signature, previewed in the composers. */
  signatureHtml: string;
};

const PeopleContext = createContext<InboxPeople>({
  staff: STAFF,
  me: CURRENT_USER,
  live: false,
  signatureHtml: "",
});

const usePeople = (): InboxPeople => useContext(PeopleContext);

/** Splits a comma or semicolon separated address field. */
const splitAddresses = (value: string): string[] =>
  value
    .split(/[,;]/)
    .map((part) => part.trim())
    .filter(Boolean);

/* ---------- small presentational helpers ---------- */

const mono = "font-mono text-[10px] uppercase tracking-[0.12em]";

function Chip({
  children,
  tone = "neutral",
  className,
}: {
  children: React.ReactNode;
  tone?: "neutral" | "green" | "amber" | "blue" | "red" | "violet";
  className?: string;
}) {
  const tones = {
    neutral: "bg-gray-100 text-gray-700 border-gray-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span
      className={cn(
        mono,
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5",
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

function categoryTone(c: Category) {
  switch (c) {
    case "enquiry":
      return "green" as const;
    case "change":
      return "blue" as const;
    case "cancellation":
      return "red" as const;
    case "invoice":
      return "amber" as const;
    case "road-show":
      return "violet" as const;
    default:
      return "neutral" as const;
  }
}

function Avatar({
  initials,
  size = "md",
}: {
  initials: string;
  size?: "sm" | "md";
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-700",
        size === "sm" ? "h-5 w-5 text-[9px]" : "h-8 w-8 text-xs",
      )}
    >
      {initials}
    </span>
  );
}

function initialsOf(name: string) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

type View =
  | { kind: "all" }
  | { kind: "assistant" }
  | { kind: "follow-ups" }
  | { kind: "replies" }
  | { kind: "actions" }
  | { kind: "mentions" }
  | { kind: "assigned" }
  | { kind: "favorites" }
  | { kind: "category"; category: Category }
  | { kind: "mailbox"; mailboxId: string; folder?: Folder };

type Folder = "inbox" | "drafts" | "sent" | "junk" | "trash";
const FOLDER_LABELS: Record<Folder, string> = {
  inbox: "Inbox",
  drafts: "Drafts",
  sent: "Sent",
  junk: "Junk",
  trash: "Trash",
};

/** Which mailbox folder a thread sits in – the wireframe has no junk/trash yet. */
function folderOf(t: Thread, mailboxAddress: string | undefined): Folder {
  if (t.gmailFolder && t.gmailFolder !== "inbox") return t.gmailFolder;
  if (t.lastDirection) return t.lastDirection === "out" ? "sent" : "inbox";
  const last = [...t.messages].reverse().find((m) => !m.internal);
  if (last && last.from.email === mailboxAddress) return "sent";
  if (t.hasDraft && !t.draft) return "drafts";
  return "inbox";
}

/**
 * Junk and trash appear only in their own mailbox folders, never in All mail,
 * categories or the other views - the same as Gmail.
 */
const isJunkOrTrash = (t: Thread) =>
  t.gmailFolder === "junk" || t.gmailFolder === "trash";

function viewTitle(v: View, mailboxes: Mailbox[]) {
  switch (v.kind) {
    case "all":
      return "All mail";
    case "assistant":
      return "Assistant";
    case "follow-ups":
      return "Follow-ups";
    case "replies":
      return "Replies";
    case "actions":
      return "Actions";
    case "mentions":
      return "Mentions";
    case "assigned":
      return "Assigned to me";
    case "favorites":
      return "Favorites";
    case "category":
      return CATEGORY_LABELS[v.category];
    case "mailbox": {
      const city =
        mailboxes.find((m) => m.id === v.mailboxId)?.city ?? "Mailbox";
      return v.folder && v.folder !== "inbox"
        ? `${city} · ${FOLDER_LABELS[v.folder]}`
        : city;
    }
  }
}

type ReplyKind = "reply" | "reply-all" | "forward";
/**
 * Detail page for a linked booking in whichever portal the inbox is mounted
 * in, or undefined when the booking is not (yet) in the database.
 */
function useBookingHref(booking?: LinkedBooking): string | undefined {
  const pathname = usePathname();
  if (!booking?.id || !booking.venueId) return undefined;
  const portal = pathname.startsWith(PORTAL_ROUTES.booking.base)
    ? PORTAL_ROUTES.booking
    : PORTAL_ROUTES.superadmin;
  return `${portal.venue(String(booking.venueId)).bookings}/${booking.id}`;
}

/** The booking opened in the cart, where Modify booking saves changes. */
function useCartHref(booking?: LinkedBooking): string | undefined {
  const pathname = usePathname();
  if (!booking?.id || !booking.venueId) return undefined;
  const portal = pathname.startsWith(PORTAL_ROUTES.booking.base)
    ? PORTAL_ROUTES.booking
    : PORTAL_ROUTES.superadmin;
  return `${portal.venue(String(booking.venueId)).base}/booking-new?bookingId=${booking.id}`;
}

/** Toolbar → thread view: open the composer with this kind. */
/** A toolbar Reply / Reply all / Forward, for the thread it was clicked on. */
type ComposeRequest = { kind: ReplyKind; n: number; threadId: string };

/** Serialise a thread as RFC 4155 mbox (internal notes are left out). */
function threadToMbox(thread: Thread) {
  return thread.messages
    .filter((m) => !m.internal)
    .map((m) =>
      [
        `From ${m.from.email} ${m.sentAt}`,
        `From: ${m.from.name} <${m.from.email}>`,
        `To: ${m.to}`,
        `Subject: ${thread.subject}`,
        `Date: ${m.sentIso ? new Date(m.sentIso).toUTCString() : m.sentAt}`,
        "",
        // Paragraphs with the blank line between them; a line starting
        // "From " is escaped so it does not read as a new message.
        m.body
          .join("\n\n")
          .split("\n")
          .map((line) => (line.startsWith("From ") ? `>${line}` : line))
          .join("\n"),
        "",
      ].join("\n"),
    )
    .join("\n");
}

function downloadMbox(thread: Thread) {
  const blob = new Blob([threadToMbox(thread)], { type: "application/mbox" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${thread.subject.replace(/[^\w-]+/g, "-").toLowerCase()}.mbox`;
  a.click();
  URL.revokeObjectURL(url);
}

async function copyThreadLink(thread: Thread) {
  const url = new URL(window.location.href);
  url.searchParams.set("thread", thread.id);
  try {
    await navigator.clipboard.writeText(url.toString());
    toast.success("Link copied");
  } catch {
    toast.error("Couldn't copy the link");
  }
}

/* ---------- main component ---------- */

export type InboxClientProps = {
  /** Threads from the database. Falls back to the wireframe's fixtures. */
  threads?: Thread[];
  /** Connected mailboxes. Falls back to the wireframe's fixtures. */
  mailboxes?: Mailbox[];
  /** People a thread can be assigned to. */
  staff?: StaffMember[];
  /** The signed-in user's auth id. */
  currentUserId?: string;
  /** The signed-in user's email signature (sanitised HTML). */
  signatureHtml?: string;
  /** Live: the conversation to open on arrival (?thread=). */
  openThreadId?: string;
  /** Live: the category view to open on arrival (?category=). */
  openCategory?: string;
  /** Live: the search to run on arrival (?q=). */
  openSearch?: string;
  /** Where the next page of threads starts; null once all are loaded. */
  nextCursor?: ConversationCursor | null;
  /** A mailbox is still importing its history from Gmail. */
  importPending?: boolean;
  /** Real totals from the database, for counts beyond what is loaded. */
  totals?: InboxCountRow[];
  /** Shown as a banner above the inbox. */
  error?: string;
  /**
   * The AI also runs by itself (Settings → AI Settings): opening a thread
   * that waits for a reply drafts one. Off, it drafts only on Suggest reply.
   */
  aiAutomatic?: boolean;
};

/**
 * How often an open inbox picks up what others and the sync changed. Faster
 * while a mailbox is still importing, so the list visibly fills in.
 */
const POLL_INTERVAL_MS = 60_000;
const IMPORT_POLL_INTERVAL_MS = 10_000;

export function InboxClient({
  threads: liveThreads,
  mailboxes: liveMailboxes,
  staff: liveStaff,
  currentUserId,
  signatureHtml = "",
  openThreadId,
  openCategory,
  openSearch = "",
  nextCursor: initialCursor = null,
  importPending = false,
  totals,
  error,
  aiAutomatic = false,
}: InboxClientProps = {}) {
  // The inbox is a fixed-height, edge-to-edge layout that never scrolls the
  // page, so drop the always-on page scrollbar (globals.css) while mounted.
  useEffect(() => {
    document.documentElement.classList.add("inbox-page");
    return () => document.documentElement.classList.remove("inbox-page");
  }, []);

  const router = useRouter();

  // Undefined means "not wired up" and keeps the wireframe. An empty array
  // means a real mailbox that happens to have nothing in it, which must
  // render as empty rather than silently falling back to fixtures.
  const live = liveThreads !== undefined;
  const initialThreads = liveThreads ?? THREADS;
  const mailboxes = liveMailboxes?.length ? liveMailboxes : MAILBOXES;

  const people = useMemo<InboxPeople>(() => {
    if (!live) return { staff: STAFF, me: CURRENT_USER, live, signatureHtml };
    const staff = liveStaff ?? [];
    const me = staff.find((s) => s.id === currentUserId) ?? {
      id: currentUserId ?? "",
      name: "Me",
      initials: "ME",
      days: "",
    };
    return { staff, me, live, signatureHtml };
  }, [live, liveStaff, currentUserId, signatureHtml]);
  const me = people.me;

  const [threads, setThreads] = useState<Thread[]>(initialThreads);
  const [view, setView] = useState<View>(() =>
    openCategory && openCategory in CATEGORY_LABELS
      ? { kind: "category", category: openCategory as Category }
      : { kind: "all" },
  );
  const [tab, setTab] = useState<"open" | "done">("open");
  // Live: nothing is open until a thread is clicked, unless a link or a
  // notification email named one. The wireframe opens its first thread so
  // the mockup shows something straight away.
  const [selectedId, setSelectedId] = useState<string>(
    live ? (openThreadId ?? "") : (initialThreads[0]?.id ?? ""),
  );
  // The search shown in the list (?q=); see the search below. Set from the
  // search popup, which "Search" in the sidebar and "/" open.
  const [search, setSearch] = useState(openSearch);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName ?? "");
      if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // The open thread, and the category it was opened from, in the address
  // bar, so a reload or a shared link comes back to the same place. Replaced
  // rather than pushed: clicking through threads does not fill the history,
  // and the page is not reloaded from the server.
  useEffect(() => {
    if (!live) return;
    const url = new URL(window.location.href);
    if (selectedId) url.searchParams.set("thread", selectedId);
    else url.searchParams.delete("thread");
    if (view.kind === "category") {
      url.searchParams.set("category", view.category);
    } else {
      url.searchParams.delete("category");
    }
    const q = search.trim();
    if (q) url.searchParams.set("q", q);
    else url.searchParams.delete("q");
    if (url.href !== window.location.href) {
      window.history.replaceState(window.history.state, "", url);
    }
  }, [live, selectedId, view, search]);

  const [composeMode, setComposeMode] = useState<"reply" | "note">("reply");
  const [appliedActions, setAppliedActions] = useState<
    Record<string, "applied" | "rejected">
  >({});
  const [autoDraft, setAutoDraft] = useState(true);
  const [savedToKb, setSavedToKb] = useState<Record<string, boolean>>({});
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeRequest, setComposeRequest] = useState<ComposeRequest | null>(
    null,
  );
  const [askOpen, setAskOpen] = useState(false);
  // Each thread's assistant chat, kept until the page is reloaded.
  const [askChats, setAskChats] = useState<Record<string, AskChat>>({});
  const updateAskChat = useCallback(
    (threadId: string, update: (chat: AskChat) => AskChat) =>
      setAskChats((chats) => ({
        ...chats,
        [threadId]: update(chats[threadId] ?? EMPTY_ASK_CHAT),
      })),
    [],
  );

  /*
   * Live threads arrive without their messages, which load when a thread is
   * opened. Messages are cached against the thread's version, so a thread
   * that changed since (a new email, a note) reloads when next opened.
   */
  const messageCache = useRef(
    new Map<string, { version?: string; messages: Message[] }>(),
  );

  // Where the next page starts. Only scrolling moves it: a refresh reloads
  // the first page, and the pages below it stay as they were.
  const [cursor, setCursor] = useState<ConversationCursor | null>(
    initialCursor,
  );
  const [loadingMore, setLoadingMore] = useState(false);

  const withCachedMessages = (t: Thread): Thread => {
    const cached = messageCache.current.get(t.id);
    return cached && cached.version === t.version
      ? { ...t, messages: cached.messages }
      : t;
  };

  // A server refresh (polling, or an action revalidating) brings a new first
  // page. Take it, keeping the threads scrolled in below it and each
  // thread's suggested draft.
  useEffect(() => {
    if (!liveThreads) return;
    setThreads((prev) =>
      mergeRefreshedPage(prev, liveThreads, withCachedMessages),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveThreads]);

  const loadMore = async () => {
    if (!live || !cursor || loadingMore) return;
    setLoadingMore(true);
    const result = await loadMoreConversations(cursor);
    setLoadingMore(false);

    if (!result.success || !result.data) {
      toast.error(result.error ?? "Could not load more threads");
      return;
    }

    const page = result.data;
    setThreads((prev) => {
      const ids = new Set(prev.map((t) => t.id));
      return [...prev, ...page.threads.filter((t) => !ids.has(t.id))].sort(
        byNewest,
      );
    });

    // The end of the list is not the end while a mailbox is importing: the
    // import adds older threads below, so keep a cursor at the oldest one
    // and the next scroll picks them up.
    const oldest = page.threads[page.threads.length - 1];
    setCursor(
      page.nextCursor ??
        (importPending
          ? oldest?.sortKey
            ? { at: oldest.sortKey, id: oldest.id }
            : cursor
          : null),
    );
  };

  // Opening the inbox syncs every mailbox in the background.
  useEffect(() => {
    if (!live) return;
    requestInboxSync().then((result) => {
      if (!result.success) {
        toast.error(result.error ?? "Could not start syncing with Gmail");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  useEffect(() => {
    if (!live) return;
    const timer = setInterval(
      () => {
        if (document.visibilityState === "visible") router.refresh();
      },
      importPending ? IMPORT_POLL_INTERVAL_MS : POLL_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [live, router, importPending]);

  const update = (id: string, patch: Partial<Thread>) =>
    setThreads((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    );

  /**
   * Applies a change locally at once, then on the server. A failure is
   * reported and the server's version reloaded, which undoes the local edit.
   */
  const persist = (
    id: string,
    patch: Partial<Thread>,
    save: () => Promise<{ success: boolean; error?: string }>,
  ) => {
    update(id, patch);
    if (!live) return;
    save().then((result) => {
      if (!result.success) {
        toast.error(result.error ?? "Could not save the change");
      }
      // Either way, reload from the server: on failure to undo the local
      // edit, on success so the sidebar totals catch up with it.
      router.refresh();
    });
  };

  // Opening a thread leaves its read state alone: it changes only through
  // the read/unread toolbar button, and never in Gmail.
  // A reply requested in another thread must not open this one's composer.
  const openThread = (id: string) => {
    setSelectedId(id);
    setComposeRequest(null);
  };

  const sendNewMail = async (mail: NewMail): Promise<boolean> => {
    if (live) {
      const input = {
        mailboxId: Number(mail.mailboxId),
        to: splitAddresses(mail.to),
        subject: mail.subject,
        html: mail.html,
      };
      const result = mail.attachments.length
        ? await sendWithAttachments("new", input, mail.attachments)
        : await sendNewMailAction(input);
      if (!result.success) {
        toast.error(result.error ?? "Could not send the mail");
        return false;
      }
      toast.success("Sent");
      setView({ kind: "all" });
      setTab("open");
      if (result.data) setSelectedId(result.data);
      setComposeOpen(false);
      return true;
    }

    // Wireframe: a sent mail becomes an outbound thread in the chosen mailbox.
    const mailbox = mailboxes.find((m) => m.id === mail.mailboxId);
    const sender = people.staff.find((s) => s.id === mail.sendAs) ?? me;
    const id = `new-${Date.now()}`;
    const thread: Thread = {
      id,
      mailboxId: mail.mailboxId,
      customer: { name: mail.to, email: mail.to, type: "Unknown" },
      subject: mail.subject,
      preview: mail.body.trim().split("\n")[0] || "(no message)",
      receivedAt: "Just now",
      category: "other",
      unread: false,
      starred: false,
      done: false,
      needsFiling: true,
      assignee: sender.id,
      msgCount: 1,
      hasDraft: false,
      messages: [
        {
          id: `${id}-m1`,
          from: { name: sender.name, email: mailbox?.address ?? "" },
          to: mail.to,
          sentAt: "Today · just now",
          body: mail.body.split(/\n{2,}/).filter(Boolean),
        },
      ],
    };
    setThreads((prev) => [thread, ...prev]);
    setView({ kind: "all" });
    setTab("open");
    setSelectedId(id);
    setComposeOpen(false);
    return true;
  };

  const hasNotes = (t: Thread) =>
    t.hasNotes ?? t.messages.some((m) => m.internal);

  const matchesView = (t: Thread) => {
    if (view.kind !== "mailbox" && isJunkOrTrash(t)) return false;
    switch (view.kind) {
      case "all":
        return true;
      case "assistant":
        return openAction(t) || Boolean(t.draft);
      case "follow-ups":
        return t.starred || t.needsFiling;
      case "replies":
        return Boolean(t.draft);
      case "actions":
        return openAction(t) && !appliedActions[t.id];
      case "mentions":
        return live
          ? Boolean(t.mentionedMe)
          : Boolean(t.mentions?.length) || hasNotes(t);
      case "assigned":
        return t.assignee === me.id;
      case "favorites":
        return t.starred;
      case "category":
        return t.category === view.category;
      case "mailbox": {
        if (t.mailboxId !== view.mailboxId) return false;
        const address = mailboxes.find((m) => m.id === view.mailboxId)?.address;
        return folderOf(t, address) === (view.folder ?? "inbox");
      }
    }
  };

  // Which Open/Done tab a thread belongs in. Mentions ignore Done: a
  // mention stays yours to clear either way, so all of them sit under Open.
  const inTab = (t: Thread, which: "open" | "done") =>
    live && view.kind === "mentions"
      ? which === "open"
      : which === "done"
        ? t.done
        : !t.done;

  const visible = useMemo(
    () =>
      threads.filter(
        (t) => matchesView(t) && inTab(t, tab),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [threads, view, tab, appliedActions],
  );

  /*
   * Search covers all mail the user can see, open and done, whatever the
   * view: live it asks the server, since only some pages are loaded. Results
   * join the loaded threads so they open like any other; the list shows them
   * by id, so read and star changes show at once.
   */
  const [searchIds, setSearchIds] = useState<string[] | null>(null);
  const [searching, setSearching] = useState(false);
  const searchTerm = search.trim();
  useEffect(() => {
    if (!searchTerm) {
      setSearchIds(null);
      setSearching(false);
      return;
    }
    if (!live) {
      // Wireframe: each word must start a word somewhere, as the server
      // matches; operators need the database.
      const startsWord = parseSearch(searchTerm).words.map(
        (word) =>
          new RegExp(
            `(^|[^\\p{L}\\p{N}])${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
            "iu",
          ),
      );
      setSearchIds(
        threads
          .filter((t) =>
            startsWord.every((re) =>
              [t.subject, t.customer.name, t.customer.email, t.preview].some(
                (field) => field && re.test(field),
              ),
            ),
          )
          .map((t) => t.id),
      );
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      const result = await searchInbox(searchTerm);
      if (cancelled) return;
      setSearching(false);
      if (!result.success || !result.data) {
        toast.error(result.error ?? "Search failed");
        return;
      }
      const found = result.data;
      setThreads((prev) => {
        const loaded = new Set(prev.map((t) => t.id));
        const added = found.filter((t) => !loaded.has(t.id));
        return added.length ? [...prev, ...added].sort(byNewest) : prev;
      });
      setSearchIds(found.map((t) => t.id));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, searchTerm]);
  const searchResults = useMemo(() => {
    if (!searchIds) return null;
    const byId = new Map(threads.map((t) => [t.id, t]));
    return searchIds.flatMap((id) => byId.get(id) ?? []);
  }, [searchIds, threads]);

  const selected =
    threads.find((t) => t.id === selectedId) ??
    (live ? undefined : visible[0]);

  // Load the opened thread's messages when they are not cached.
  useEffect(() => {
    if (!live || !selected) return;
    const cached = messageCache.current.get(selected.id);
    if (cached && cached.version === selected.version) return;

    let cancelled = false;
    const { id, version } = selected;
    getConversationMessages(id).then((result) => {
      if (cancelled) return;
      if (!result.success || !result.data) {
        toast.error(result.error ?? "Could not load the conversation");
        return;
      }
      messageCache.current.set(id, { version, messages: result.data });
      update(id, { messages: result.data });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, selected?.id, selected?.version]);

  // The AI's suggested reply for the opened thread. Fetched on every open:
  // the drafter writes it in the background without changing the thread.
  // With automatic AI on, a thread waiting for our reply that has none yet
  // gets one written now, once per thread per visit, so staff never have to
  // ask for it. Off, staff ask with Suggest reply.
  const autoDrafted = useRef(new Set<string>());
  useEffect(() => {
    if (!live || !selected) return;
    let cancelled = false;
    const { id } = selected;
    getSuggestedDraft(id).then((result) => {
      if (cancelled || !result.success) return;
      if (result.data) {
        update(id, { draft: result.data });
        return;
      }
      update(id, { draft: undefined });
      if (
        !aiAutomatic ||
        !awaitsReply(selected) ||
        autoDrafted.current.has(id)
      ) {
        return;
      }

      autoDrafted.current.add(id);
      update(id, { draftWriting: true });
      // Not cancelled on leaving the thread: the draft is stored either way,
      // and shows when the thread is opened again.
      regenerateDraft(id)
        .catch((caught) => ({
          success: false as const,
          data: undefined,
          error: caught instanceof Error ? caught.message : String(caught),
        }))
        .then((written) => {
          update(id, {
            draftWriting: false,
            ...(written.success && written.data ? { draft: written.data } : {}),
          });
          // Said out loud, so a failed draft is not just a spinner that
          // quietly goes away.
          if (!written.success || !written.data) {
            toast.error(
              `Could not write a suggested reply: ${written.error ?? "unknown error"}`,
            );
          }
        });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, selected?.id, selected?.version]);

  // Everything but junk and trash, for the counts of views that leave them
  // out.
  const mail = threads.filter((t) => !isJunkOrTrash(t));

  /*
   * Live counts come from database totals, since only the scrolled-to
   * threads are loaded. The views totals cannot answer - favorites, mentions,
   * unread - depend on per-person state and still count loaded threads.
   */
  const useTotals = live && totals !== undefined;
  const sum = (match: (row: InboxCountRow) => boolean) =>
    (totals ?? []).reduce(
      (total, row) => (match(row) ? total + row.conversations : total),
      0,
    );
  const rowIsMail = (row: InboxCountRow) =>
    row.folder !== "junk" && row.folder !== "trash";
  const rowFolder = (row: InboxCountRow): Folder =>
    row.folder !== "inbox"
      ? row.folder
      : row.last_direction === "out"
        ? "sent"
        : "inbox";
  const rowInView = (row: InboxCountRow): boolean | null => {
    switch (view.kind) {
      case "all":
        return rowIsMail(row);
      case "category":
        return rowIsMail(row) && row.kind === view.category;
      case "assigned":
        return rowIsMail(row) && row.assigned_to_me;
      case "mailbox":
        return (
          String(row.mailbox_account_id) === view.mailboxId &&
          rowFolder(row) === (view.folder ?? "inbox")
        );
      default:
        return null;
    }
  };
  // Tabs for views the totals cover; the rest count loaded threads.
  const viewHasTotals =
    useTotals &&
    ["all", "category", "assigned", "mailbox"].includes(view.kind);

  // Sidebar counts are unread, as in Gmail; Drafts counts every draft.
  const counts = {
    all: useTotals
      ? sum((r) => rowIsMail(r) && r.status === "open" && r.unread)
      : mail.filter((t) => !t.done).length,
    open: viewHasTotals
      ? sum((r) => rowInView(r) === true && r.status === "open")
      : threads.filter((t) => matchesView(t) && inTab(t, "open")).length,
    done: viewHasTotals
      ? sum((r) => rowInView(r) === true && r.status === "closed")
      : threads.filter((t) => matchesView(t) && inTab(t, "done")).length,
    assistant: mail.filter((t) => (openAction(t) || t.draft) && !t.done)
      .length,
    followUps: mail.filter((t) => (t.starred || t.needsFiling) && !t.done)
      .length,
    replies: mail.filter((t) => t.draft && !t.done).length,
    actions: mail.filter((t) => openAction(t) && !appliedActions[t.id] && !t.done)
      .length,
    mentions: live
      ? mail.filter((t) => t.mentionedMe).length
      : mail.filter((t) => hasNotes(t) && !t.done).length,
    assigned: useTotals
      ? sum(
          (r) =>
            rowIsMail(r) && r.assigned_to_me && r.status === "open" && r.unread,
        )
      : mail.filter((t) => t.assignee === me.id && !t.done).length,
    category: (c: Category) =>
      useTotals
        ? sum(
            (r) =>
              rowIsMail(r) && r.kind === c && r.status === "open" && r.unread,
          )
        : mail.filter((t) => t.category === c && !t.done).length,
    mailbox: (id: string) =>
      useTotals
        ? sum(
            (r) =>
              String(r.mailbox_account_id) === id &&
              rowIsMail(r) &&
              r.status === "open" &&
              r.unread,
          )
        : mail.filter((t) => t.mailboxId === id && !t.done).length,
    folder: (id: string, folder: Folder) => {
      if (useTotals) {
        return sum(
          (r) =>
            String(r.mailbox_account_id) === id &&
            rowFolder(r) === folder &&
            r.status === "open" &&
            (folder === "drafts" || r.unread),
        );
      }
      const address = mailboxes.find((m) => m.id === id)?.address;
      return threads.filter(
        (t) => t.mailboxId === id && !t.done && folderOf(t, address) === folder,
      ).length;
    },
  };

  return (
    <PeopleContext.Provider value={people}>
    <MailboxContext.Provider value={mailboxes}>
      <div className="pb-6 print:pb-0">
        <div className="flex h-[calc(100vh-4rem-1.5rem)] flex-col rounded-xl border border-gray-200 bg-white text-[13px] text-gray-900 shadow-sm print:h-auto print:rounded-none print:border-0 print:shadow-none">
          {error && (
            <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {error}
              </span>
            </div>
          )}

          <SearchDialog
            open={searchOpen}
            onOpenChange={setSearchOpen}
            value={search}
            onSearch={setSearch}
          />

          {/* Action row spans all three panes, as in the reference inbox. */}
          <NewMailDialog
            open={composeOpen}
            onOpenChange={setComposeOpen}
            onSend={sendNewMail}
          />
          <Toolbar
            thread={selected}
            onCompose={() => setComposeOpen(true)}
            onReply={(kind) =>
              selected &&
              setComposeRequest({ kind, n: Date.now(), threadId: selected.id })
            }
            onDone={() =>
              selected &&
              persist(selected.id, { done: !selected.done }, () =>
                setConversationDone(selected.id, !selected.done),
              )
            }
            onStar={() =>
              selected &&
              persist(selected.id, { starred: !selected.starred }, () =>
                setConversationStar(selected.id, !selected.starred),
              )
            }
            onMarkForReview={() =>
              selected &&
              persist(
                selected.id,
                { markedForReview: !selected.markedForReview },
                () =>
                  setConversationMarkedForReview(
                    selected.id,
                    !selected.markedForReview,
                  ),
              )
            }
            onAssign={(id) =>
              selected &&
              persist(selected.id, { assignee: id }, () =>
                setConversationAssignee(selected.id, id ?? null),
              )
            }
            onMove={(category) =>
              selected &&
              persist(
                selected.id,
                { category, categoryByAi: false, needsFiling: false },
                () =>
                setConversationKind(selected.id, category),
              )
            }
            onToggleRead={() =>
              selected &&
              persist(selected.id, { unread: !selected.unread }, () =>
                selected.unread
                  ? markConversationRead(selected.id)
                  : markConversationUnread(selected.id),
              )
            }
          />
          <AskAssistantSheet
            key={selected?.id}
            thread={selected}
            chat={askChats[selected?.id ?? ""]}
            onChatChange={updateAskChat}
            open={askOpen}
            onOpenChange={setAskOpen}
          />
          <div className="flex min-h-0 flex-1">
            <Sidebar
              view={view}
              setView={(v) => {
                setView(v);
                setTab("open");
                setSearch("");
              }}
              counts={counts}
              search={search.trim()}
              onOpenSearch={() => setSearchOpen(true)}
              onClearSearch={() => setSearch("")}
            />
            <ThreadList
              title={searchTerm ? "Search" : viewTitle(view, mailboxes)}
              threads={searchResults ?? (searchTerm ? [] : visible)}
              search={search}
              searching={searching}
              tab={tab}
              setTab={setTab}
              counts={{ open: counts.open, done: counts.done }}
              selectedId={selected?.id}
              onSelect={openThread}
              onLoadMore={loadMore}
              loadingMore={loadingMore}
              hasMore={live && cursor !== null && !searchTerm}
              importing={live && importPending}
              onCompose={() => setComposeOpen(true)}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              {selected ? (
                <div className="flex min-h-0 flex-1">
                  <ThreadView
                    key={selected.id}
                    thread={selected}
                    aiAutomatic={aiAutomatic}
                    autoDraft={autoDraft}
                    setAutoDraft={setAutoDraft}
                    actionState={appliedActions[selected.id]}
                    onAction={(state) =>
                      setAppliedActions((p) => ({ ...p, [selected.id]: state }))
                    }
                    composeMode={composeMode}
                    setComposeMode={setComposeMode}
                    savedToKb={Boolean(savedToKb[selected.id])}
                    onSaveToKb={() =>
                      setSavedToKb((p) => ({ ...p, [selected.id]: true }))
                    }
                    onAssign={(id) =>
                      persist(selected.id, { assignee: id }, () =>
                        setConversationAssignee(selected.id, id),
                      )
                    }
                    composeRequest={composeRequest}
                    onDraft={(draft) => update(selected.id, { draft })}
                    onAsk={() => setAskOpen(true)}
                  />
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center text-gray-400">
                  {live ? "Select a conversation to read it" : "Nothing in this view"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MailboxContext.Provider>
    </PeopleContext.Provider>
  );
}

/* ---------- sidebar ---------- */

function Sidebar({
  view,
  setView,
  counts,
  search,
  onOpenSearch,
  onClearSearch,
}: {
  view: View;
  setView: (v: View) => void;
  counts: any;
  /** The search in effect; empty when none. */
  search: string;
  onOpenSearch: () => void;
  onClearSearch: () => void;
}) {
  const mailboxes = useMailboxes();
  // While searching, the results replace every view, so none is current.
  const is = (v: View) =>
    !search && JSON.stringify(v) === JSON.stringify(view);

  const Row = ({
    v,
    icon: Icon,
    label,
    count,
    indent,
    muted,
  }: {
    v: View;
    icon?: any;
    label: string;
    count?: number;
    indent?: boolean;
    muted?: boolean;
  }) => (
    <button
      onClick={() => setView(v)}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-gray-100",
        indent && "pl-7",
        is(v) && "bg-gray-100 font-medium",
        muted && "text-gray-400",
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5 text-gray-500" />}
      <span className="flex-1 truncate">{label}</span>
      {count !== undefined && count > 0 && (
        <span className="text-[11px] text-gray-400">{count}</span>
      )}
    </button>
  );

  const Section = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <div className="mt-4">
      <div className={cn(mono, "mb-1 px-2 text-gray-400")}>{title}</div>
      {children}
    </div>
  );

  return (
    <div className="flex w-56 shrink-0 flex-col border-r border-gray-200 bg-gray-50/60 print:hidden">
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        <div className="mt-3">
          <Row
            v={{ kind: "all" }}
            icon={Inbox}
            label="All mail"
            count={counts.all}
          />
          <div
            className={cn(
              "group flex w-full items-center rounded hover:bg-gray-100",
              search && "bg-gray-100 font-medium",
            )}
          >
            <button
              onClick={onOpenSearch}
              title={search ? `Search: ${search}` : "Search mail (/)"}
              className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1 text-left"
            >
              <Search className="h-3.5 w-3.5 shrink-0 text-gray-500" />
              <span className="flex-1 truncate">{search || "Search"}</span>
              {!search && (
                <kbd className="rounded border border-gray-200 px-1 font-mono text-[10px] text-gray-400">
                  /
                </kbd>
              )}
            </button>
            {search && (
              <button
                onClick={onClearSearch}
                className="mr-1 rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <Section title="Views">
          <Row
            v={{ kind: "assistant" }}
            icon={Sparkles}
            label="Assistant"
            count={counts.assistant}
          />
          <Row
            v={{ kind: "follow-ups" }}
            label="Follow-ups"
            count={counts.followUps}
            indent
          />
          <Row
            v={{ kind: "replies" }}
            label="Replies"
            count={counts.replies}
            indent
          />
          <Row
            v={{ kind: "actions" }}
            label="Actions"
            count={counts.actions}
            indent
          />
          <Row v={{ kind: "favorites" }} icon={Star} label="Favorites" />
          <Row
            v={{ kind: "mentions" }}
            icon={AtSign}
            label="Mentions"
            count={counts.mentions}
          />
          <Row
            v={{ kind: "assigned" }}
            icon={UserPlus}
            label="Assigned to me"
            count={counts.assigned}
          />
        </Section>

        <Section title="Categories · open">
          {(Object.keys(CATEGORY_LABELS) as Category[]).map((c) => (
            <Row
              key={c}
              v={{ kind: "category", category: c }}
              label={CATEGORY_LABELS[c]}
              count={counts.category(c)}
              indent
            />
          ))}
        </Section>

        <Section title="Mailboxes">
          {mailboxes.map((m) => (
            <div key={m.id}>
              <button
                onClick={() =>
                  m.synced && setView({ kind: "mailbox", mailboxId: m.id })
                }
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-gray-100",
                  is({ kind: "mailbox", mailboxId: m.id }) &&
                    "bg-gray-100 font-medium",
                )}
              >
                <ChevronRight className="h-3 w-3 text-gray-400" />
                <span className="flex-1 truncate text-xs">{m.address}</span>
                {m.synced ? (
                  <span className="text-[11px] text-gray-400">
                    {counts.mailbox(m.id)}
                  </span>
                ) : (
                  <span className={cn(mono, "text-gray-400")}>not synced</span>
                )}
              </button>
              {m.synced &&
                view.kind === "mailbox" &&
                view.mailboxId === m.id && (
                  <div className="pl-5 text-gray-500">
                    {(
                      [
                        ["inbox", Inbox],
                        ["drafts", FileText],
                        ["sent", Send],
                        ["junk", ShieldAlert],
                        ["trash", Trash2],
                      ] as [Folder, any][]
                    ).map(([folder, Icon]) => {
                      const active = (view.folder ?? "inbox") === folder;
                      const count = counts.folder(m.id, folder);
                      return (
                        <button
                          key={folder}
                          onClick={() =>
                            setView({
                              kind: "mailbox",
                              mailboxId: m.id,
                              folder,
                            })
                          }
                          className={cn(
                            "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-gray-100",
                            active && "bg-gray-100 font-medium text-gray-900",
                          )}
                        >
                          <Icon className="h-3 w-3" />
                          <span className="flex-1">
                            {FOLDER_LABELS[folder]}
                          </span>
                          {count > 0 && (
                            <span className="text-[11px] text-gray-400">
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

/* ---------- thread list ---------- */

/**
 * Gmail-style search in a popup, opened from "Search" in the sidebar or by
 * pressing "/": words, "phrases", -exclusions and operators (from:,
 * is:unread, after: ...), with a guide to them. Enter runs the search, which
 * covers all mail the user can see, whatever view is open.
 */
function SearchDialog({
  open,
  onOpenChange,
  value,
  onSearch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The search now shown in the list; empty when none. */
  value: string;
  onSearch: (text: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(value);
  // Each opening starts from the search in effect.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const run = (text: string) => {
    onSearch(text.trim());
    onOpenChange(false);
  };
  const add = (operator: string) => {
    const next = `${draft.trim()} ${operator.split("  ")[0]}`.trim();
    setDraft(next.endsWith(":") ? next : `${next} `);
    inputRef.current?.focus();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12%] max-w-xl translate-y-0 gap-0 overflow-hidden p-0 text-[13px] [&>button]:hidden">
        <DialogTitle className="sr-only">Search mail</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run(draft);
          }}
          className="relative border-b border-gray-200"
        >
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            ref={inputRef}
            autoFocus
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search mail - try from:sundus is:unread"
            aria-label="Search mail"
            className="h-12 w-full bg-transparent pl-11 pr-12 text-sm placeholder:text-gray-400 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
          />
          {draft && (
            <button
              type="button"
              onClick={() => {
                setDraft("");
                inputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              title="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </form>
        <div className="px-4 pb-1 pt-2.5 text-xs text-gray-500">
          Combine any of these - click one to add it. Press Enter to search.
        </div>
        <div className="max-h-[50vh] overflow-y-auto pb-2">
          {SEARCH_OPERATORS.map(([example, meaning]) => (
            <button
              key={example}
              type="button"
              onClick={() => add(example)}
              className="flex w-full items-baseline gap-3 px-4 py-1.5 text-left hover:bg-gray-50"
            >
              <code className="w-56 shrink-0 font-mono text-[12px] text-gray-900">
                {example}
              </code>
              <span className="text-xs text-gray-500">{meaning}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 border-t border-gray-200 bg-gray-50/70 px-4 py-2.5">
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => run("")}
              className="h-8 text-xs"
            >
              Clear search
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="ml-auto h-8 text-xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => run(draft)}
            disabled={!draft.trim()}
            className="h-8 gap-1.5 bg-primary text-xs hover:bg-primary/90"
          >
            <Search className="h-3.5 w-3.5" /> Search
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ThreadList({
  title,
  threads,
  tab,
  setTab,
  counts,
  selectedId,
  onSelect,
  onCompose,
  onLoadMore,
  loadingMore = false,
  hasMore = false,
  importing = false,
  search,
  searching = false,
}: {
  title: string;
  threads: Thread[];
  /** What is typed in the search bar; non-empty while searching. */
  search: string;
  /** A search is on its way to the server. */
  searching?: boolean;
  tab: "open" | "done";
  setTab: (t: "open" | "done") => void;
  counts: { open: number; done: number };
  selectedId?: string;
  onSelect: (id: string) => void;
  onCompose: () => void;
  /** Called when the list is scrolled near its bottom. */
  onLoadMore?: () => void;
  loadingMore?: boolean;
  hasMore?: boolean;
  /** Mail is still being imported, so an empty list is not the end. */
  importing?: boolean;
}) {
  type Quick = "all" | "unread" | "starred" | "draft" | "action";
  const QUICK: [Quick, string][] = [
    ["all", "Everything"],
    ["unread", "Unread"],
    ["starred", "Favorites"],
    ["draft", "Has suggested draft"],
    ["action", "Has suggested action"],
  ];
  const { staff, live } = usePeople();
  const router = useRouter();
  const [assigneeFilter, setAssigneeFilter] = useState<string>("anyone");
  const [quick, setQuick] = useState<Quick>("all");
  const [refreshing, startRefresh] = useTransition();
  // Live: pull new mail from Gmail now rather than waiting for the schedule.
  const refresh = () =>
    startRefresh(async () => {
      if (!live) {
        await new Promise((resolve) => setTimeout(resolve, 700));
        return;
      }
      const result = await syncInboxNow();
      if (!result.success) toast.error(result.error ?? "Sync failed");
      else if (result.data) toast.success(`${result.data} new messages`);
      router.refresh();
    });

  const filtered = threads.filter((t) => {
    if (assigneeFilter === "unassigned" && t.assignee) return false;
    if (
      assigneeFilter !== "anyone" &&
      assigneeFilter !== "unassigned" &&
      t.assignee !== assigneeFilter
    )
      return false;
    switch (quick) {
      case "unread":
        return t.unread;
      case "starred":
        return t.starred;
      case "draft":
        return Boolean(t.draft);
      case "action":
        return openAction(t);
      default:
        return true;
    }
  });
  const filing = filtered.filter((t) => t.needsFiling);
  const rest = filtered.filter((t) => !t.needsFiling);
  const filterActive = assigneeFilter !== "anyone" || quick !== "all";
  const assigneeLabel =
    assigneeFilter === "anyone"
      ? null
      : assigneeFilter === "unassigned"
        ? "Unassigned"
        : (staff.find((st) => st.id === assigneeFilter)?.name ?? "");

  return (
    <div className="flex w-[340px] shrink-0 flex-col border-r border-gray-200 print:hidden">
      <div className="border-b border-gray-200 px-3 pt-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-gray-300"
            />
            <span className="text-base font-semibold">{title}</span>
          </div>
          <button
            onClick={onCompose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            title="New mail"
          >
            <PenSquare className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between pb-2">
          <div
            className={cn(
              "flex overflow-hidden rounded border border-gray-200",
              search.trim() && "invisible",
            )}
          >
            {(["open", "done"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  mono,
                  "px-2 py-1",
                  tab === t
                    ? "bg-gray-100 font-semibold text-gray-900"
                    : "text-gray-500",
                )}
              >
                {t} {counts[t]}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-gray-400">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "rounded p-1 hover:bg-gray-100 hover:text-gray-700",
                    assigneeFilter !== "anyone" && "text-primary",
                  )}
                  title="Filter by assignee"
                >
                  <Users className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto"
              >
                {[
                  ["anyone", "Anyone"],
                  ["unassigned", "Unassigned"],
                  ...staff.map((st) => [st.id, st.name] as [string, string]),
                ].map(([id, label]) => (
                  <DropdownMenuItem
                    key={id}
                    className="gap-2 text-[13px]"
                    onSelect={() => setAssigneeFilter(id)}
                  >
                    <Check
                      className={cn(
                        "h-3.5 w-3.5",
                        assigneeFilter === id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "rounded p-1 hover:bg-gray-100 hover:text-gray-700",
                    quick !== "all" && "text-primary",
                  )}
                  title="Filter"
                >
                  <Filter className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {QUICK.map(([id, label]) => (
                  <DropdownMenuItem
                    key={id}
                    className="gap-2 text-[13px]"
                    onSelect={() => setQuick(id)}
                  >
                    <Check
                      className={cn(
                        "h-3.5 w-3.5",
                        quick === id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={refresh}
              className="rounded p-1 hover:bg-gray-100 hover:text-gray-700"
              title="Refresh"
            >
              <RefreshCw
                className={cn("h-3.5 w-3.5", refreshing && "animate-spin")}
              />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 pb-2 text-[11px] italic text-gray-400">
          {search.trim() ? (
            <span>
              {searching
                ? "Searching…"
                : `${filtered.length} ${filtered.length === 1 ? "result" : "results"}, newest first`}
              {!searching && parseSearch(search).ignored.length > 0 && (
                <span className="not-italic text-amber-700">
                  {" "}
                  · not supported: {parseSearch(search).ignored.join(" ")}
                </span>
              )}
            </span>
          ) : filterActive ? (
            <>
              <span>
                Showing {QUICK.find(([id]) => id === quick)?.[1].toLowerCase()}
                {assigneeLabel && ` · ${assigneeLabel}`}
              </span>
              <button
                onClick={() => {
                  setAssigneeFilter("anyone");
                  setQuick("all");
                }}
                className="not-italic text-primary hover:underline"
              >
                Clear
              </button>
            </>
          ) : (
            <span>
              {live
                ? "Newest first"
                : "Sorted by assistant · unread and unanswered first"}
            </span>
          )}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (
            hasMore &&
            !loadingMore &&
            el.scrollTop + el.clientHeight >= el.scrollHeight - 200
          ) {
            onLoadMore?.();
          }
        }}
      >
        {filing.length > 0 && (
          <div className="border-b border-amber-100 bg-amber-50/70">
            <div
              className={cn(
                mono,
                "flex items-center gap-2 px-3 py-1.5 text-amber-800",
              )}
            >
              <ShieldAlert className="h-3 w-3" /> Needs filing – which booking?
              <span className="ml-auto">{filing.length}</span>
            </div>
          </div>
        )}
        {[...filing, ...rest].map((t) => (
          <ThreadRow
            key={t.id}
            t={t}
            selected={t.id === selectedId}
            onClick={() => onSelect(t.id)}
          />
        ))}
        {threads.length === 0 && !loadingMore && (
          <div className="p-6 text-center text-xs text-gray-400">
            {importing ? (
              <span className="inline-flex items-center gap-2">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Importing mail from Gmail…
              </span>
            ) : search.trim() ? (
              searching ? "Searching…" : "Nothing matches your search."
            ) : (
              "No threads here."
            )}
          </div>
        )}
        {threads.length > 0 && importing && !hasMore && (
          <div className="flex items-center justify-center gap-2 p-4 text-xs text-gray-400">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Importing older mail from Gmail…
          </div>
        )}
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 p-4 text-xs text-gray-400">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Loading more…
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadRow({
  t,
  selected,
  onClick,
}: {
  t: Thread;
  selected: boolean;
  onClick: () => void;
}) {
  const mailboxes = useMailboxes();
  const { staff } = usePeople();
  const assignee = staff.find((s) => s.id === t.assignee);
  const mailbox = mailboxes.find((m) => m.id === t.mailboxId);
  return (
    <button
      onClick={onClick}
      className={cn(
        "block w-full border-b border-gray-100 px-3 py-2.5 text-left hover:bg-gray-50",
        selected && "bg-gray-100 hover:bg-gray-100",
        t.needsFiling && "bg-amber-50/40",
      )}
    >
      <div className="flex items-center gap-2">
        {t.unread ? (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
        ) : (
          <span className="h-1.5 w-1.5 shrink-0" />
        )}
        <span
          className={cn(
            "flex-1 truncate",
            t.unread ? "font-semibold" : "font-medium",
          )}
        >
          {t.customer.name}
        </span>
        <Chip tone={categoryTone(t.category)}>
          {CATEGORY_LABELS[t.category]}
        </Chip>
        <span className="shrink-0 text-[11px] text-gray-400">
          <LocalTime iso={t.sortKey} fallback={t.receivedAt} kind="list" />
        </span>
      </div>
      <div className="mt-0.5 truncate pl-3.5 text-xs italic text-gray-500">
        {t.subject}
      </div>
      {assignee && (
        <div className="mt-1 flex items-center gap-1.5 pl-3.5">
          <Avatar initials={assignee.initials} size="sm" />
          <span className="text-[11px] text-gray-500">{assignee.name}</span>
        </div>
      )}
      <div className="mt-1 flex items-start gap-2 pl-3.5">
        <p className="line-clamp-2 flex-1 text-xs text-gray-600">{t.preview}</p>
        {t.msgCount > 1 && (
          <span className="mt-0.5 shrink-0 rounded bg-primary px-1.5 text-[10px] font-semibold text-white">
            {t.msgCount}
          </span>
        )}
      </div>
      <div
        className={cn(
          mono,
          "mt-1.5 flex flex-wrap items-center gap-2 pl-3.5 text-gray-400",
        )}
      >
        <span>{mailbox?.city}@</span>
        {openAction(t) && !t.action?.stale && (
          <span className="flex items-center gap-1 text-sky-700">
            <Sparkles className="h-3 w-3" /> Action suggested
          </span>
        )}
        {t.hasDraft && (
          <span className="flex items-center gap-1 text-sky-700">
            <Bot className="h-3 w-3" /> Draft
          </span>
        )}
        {t.needsFiling && <span className="text-amber-700">Needs booking</span>}
        {t.markedForReview && (
          <span className="flex items-center gap-1 text-violet-700">
            <ClipboardCheck className="h-3 w-3" /> For review
          </span>
        )}
        {!t.done && t.lastDirection !== "out" && (
          <span className="text-amber-600">Unanswered</span>
        )}
      </div>
    </button>
  );
}

/* ---------- toolbar ---------- */

function Toolbar({
  thread,
  onCompose,
  onReply,
  onDone,
  onStar,
  onMarkForReview,
  onAssign,
  onMove,
  onToggleRead,
}: {
  thread?: Thread;
  onCompose: () => void;
  onReply: (kind: ReplyKind) => void;
  onDone: () => void;
  onStar: () => void;
  onMarkForReview: () => void;
  onAssign: (id: string | undefined) => void;
  onMove: (category: Category) => void;
  onToggleRead: () => void;
}) {
  const { staff, me } = usePeople();
  const Item = ({ icon: Icon, label, onClick, active }: any) => (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100",
        active && "bg-primary text-white hover:bg-primary/90",
      )}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
  return (
    <div className="sticky top-16 z-20 flex h-11 shrink-0 items-center gap-1 rounded-t-xl border-b border-gray-200 bg-white px-3 print:hidden">
      <Button
        size="sm"
        className="mr-1 h-7 gap-1.5 bg-primary text-xs hover:bg-primary/90"
        onClick={onCompose}
      >
        <PenSquare className="h-3.5 w-3.5" /> New mail
      </Button>
      <Item icon={Reply} label="Reply" onClick={() => onReply("reply")} />
      <Item
        icon={ReplyAll}
        label="Reply all"
        onClick={() => onReply("reply-all")}
      />
      <Item icon={Forward} label="Forward" onClick={() => onReply("forward")} />
      <span className="mx-1 h-5 w-px bg-gray-200" />
      <Item
        icon={CheckCircle2}
        label={thread?.done ? "Reopen" : "Done"}
        onClick={onDone}
        active={!thread?.done}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 data-[state=open]:bg-gray-100">
            <Archive className="h-3.5 w-3.5" /> Move to
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <div className={cn(mono, "px-2 py-1.5 text-gray-400")}>Category</div>
          {(Object.keys(CATEGORY_LABELS) as Category[]).map((c) => (
            <DropdownMenuItem
              key={c}
              className="gap-2 text-[13px]"
              onSelect={() => onMove(c)}
            >
              <Chip tone={categoryTone(c)}>{CATEGORY_LABELS[c]}</Chip>
              {thread?.category === c && (
                <Check className="ml-auto h-3.5 w-3.5 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        onClick={onStar}
        className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
        title="Favorite"
      >
        <Star
          className={cn(
            "h-3.5 w-3.5",
            thread?.starred && "fill-amber-400 text-amber-400",
          )}
        />
      </button>
      <button
        onClick={onToggleRead}
        className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
        title={thread?.unread ? "Mark as read" : "Mark as unread"}
      >
        {thread?.unread ? (
          <Mail className="h-3.5 w-3.5" />
        ) : (
          <MailOpen className="h-3.5 w-3.5" />
        )}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 data-[state=open]:bg-gray-100">
            <UserPlus className="h-3.5 w-3.5" /> Assign
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-56 max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto"
        >
          {staff.map((st) => (
            <DropdownMenuItem
              key={st.id}
              className="gap-2 text-[13px]"
              onSelect={() => onAssign(st.id)}
            >
              <Avatar initials={st.initials} size="sm" />
              <span className="flex-1">
                {st.id === me.id ? "Me" : st.name}
              </span>
              {thread?.assignee === st.id ? (
                <Check className="h-3.5 w-3.5 text-primary" />
              ) : (
                <span className="text-[10px] text-gray-400">{st.days}</span>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            className="gap-2 text-[13px] text-gray-500"
            onSelect={() => onAssign(undefined)}
          >
            <UserPlus className="h-3.5 w-3.5" /> Unassign
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Item
        icon={ClipboardCheck}
        label={thread?.markedForReview ? "Unmark for review" : "Mark for review"}
        onClick={onMarkForReview}
      />
      <span className="mx-1 h-5 w-px bg-gray-200" />
      <button
        onClick={() => window.print()}
        className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
        title="Print"
      >
        <Printer className="h-3.5 w-3.5" />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900"
            title="More"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem
            className="gap-2 text-[13px]"
            onSelect={() => window.print()}
          >
            <Printer className="h-3.5 w-3.5 text-gray-500" /> Print thread
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2 text-[13px]"
            disabled={!thread}
            onSelect={() => thread && downloadMbox(thread)}
          >
            <Download className="h-3.5 w-3.5 text-gray-500" /> Download .mbox
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2 text-[13px]"
            disabled={!thread}
            onSelect={() => thread && copyThreadLink(thread)}
          >
            <Link2 className="h-3.5 w-3.5 text-gray-500" /> Copy link
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/* ---------- ask assistant ---------- */

type AskMessage = {
  role: "you" | "assistant";
  text: string;
  /** The question this answers, for feedback. Unset on errors. */
  answers?: string;
};

type AskChat = { messages: AskMessage[]; thinking: boolean };

const EMPTY_ASK_CHAT: AskChat = { messages: [], thinking: false };

/** Wireframe answers built from what the thread already knows. */
function answerFor(
  thread: Thread | undefined,
  question: string,
  staff: StaffMember[],
): string {
  const q = question.toLowerCase();
  if (!thread) return "Pick a thread first and I'll answer about it.";
  if (/summar|what.*(want|ask)|tl;?dr/.test(q)) {
    return `${thread.customer.name}${
      thread.customer.company ? ` (${thread.customer.company})` : ""
    } wrote about "${thread.subject}". ${thread.preview} Category: ${
      CATEGORY_LABELS[thread.category]
    }.${thread.action ? ` Suggested action: ${thread.action.title}.` : ""}`;
  }
  if (/avail|slot|time|date|when/.test(q)) {
    if (!thread.availability)
      return "There's no availability check on this thread yet – ask me to check a date and I'll look at the live schedule.";
    const free = thread.availability.slots.filter((s) => s.status !== "booked");
    return `${thread.availability.heading}: ${free
      .map((s) => `${s.time} ${s.studio} (${s.capacity}, ${s.price})`)
      .join("; ")}. ${
      free.find((s) => s.status === "recommended")
        ? `I'd recommend ${free.find((s) => s.status === "recommended")!.studio}.`
        : ""
    }`;
  }
  if (/booking|reference|paid|invoice|total/.test(q)) {
    if (!thread.booking)
      return "This thread isn't linked to a booking. Use Move to → a category or link it from the needs-filing card and I'll pull the booking in.";
    const b = thread.booking;
    return `${b.reference}: ${b.show} in ${b.studio} on ${b.date} at ${b.time}, ${b.participants} people (min ${b.minParticipants}), ${b.language}. Status ${b.status}, ${b.total}, ${b.paidStatus}${
      b.invoiceRef ? ` (invoice ${b.invoiceRef})` : ""
    }.`;
  }
  if (/draft|reply|answer|respond/.test(q)) {
    return thread.draft
      ? `There's a suggested draft ("${thread.draft.label}") based on ${thread.draft.sources.join(", ")}. Use Copy to composer to edit and send it.`
      : "No draft yet – turn on Auto-draft in the thread header or ask me to write one.";
  }
  if (/who|assign|owner/.test(q)) {
    const a = staff.find((s) => s.id === thread.assignee);
    return a
      ? `${a.name} owns this thread (${a.days}).`
      : "Nobody owns this thread yet – use Assign in the toolbar.";
  }
  return `I can summarise the thread, check availability, look up the linked booking, or explain the suggested draft. Try one of the prompts above – I only answer from this thread and the knowledge base, nothing is sent or changed.`;
}

function AskAssistantSheet({
  thread,
  chat = EMPTY_ASK_CHAT,
  onChatChange,
  open,
  onOpenChange,
}: {
  thread?: Thread;
  /** This thread's chat; the inbox keeps it when you switch threads. */
  chat?: AskChat;
  onChatChange: (
    threadId: string,
    update: (chat: AskChat) => AskChat,
  ) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { staff, live } = usePeople();
  const { messages, thinking } = chat;
  const [input, setInput] = useState("");
  // Bound to the thread asked about, so an answer that arrives after
  // switching threads still goes to that thread's chat.
  const threadId = thread?.id ?? "";
  const addMessage = (message: AskMessage, done = false) =>
    onChatChange(threadId, (c) => ({
      messages: [...c.messages, message],
      thinking: done ? false : c.thinking,
    }));
  const prompts = live
    ? [
        "Summarise this thread",
        "What does the customer want?",
        "Show the linked booking",
        "Which booking is this about?",
        "Who owns this?",
      ]
    : [
        "Summarise this thread",
        "What availability did we find?",
        "Show the linked booking",
        "Why this draft?",
        "Who owns this?",
      ];

  const ask = async (question: string) => {
    const text = question.trim();
    if (!text || thinking) return;
    const history = messages;
    onChatChange(threadId, (c) => ({
      messages: [...c.messages, { role: "you", text }],
      thinking: true,
    }));
    setInput("");

    if (!live || !thread) {
      setTimeout(() => {
        addMessage(
          {
            role: "assistant",
            text: answerFor(thread, text, staff),
            answers: text,
          },
          true,
        );
      }, 500);
      return;
    }

    // Live: Claude answers from the thread and the bookings it looks up.
    const result = await askAssistant({
      conversationId: thread.id,
      question: text,
      history,
    });
    addMessage(
      result.success
        ? { role: "assistant", text: result.data ?? "", answers: text }
        : {
            role: "assistant",
            text: result.error ?? "The assistant could not answer just now.",
          },
      true,
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-[26rem] flex-col p-0">
        <SheetTitle className="sr-only">Ask assistant</SheetTitle>
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="min-w-0">
            <div className="text-sm font-semibold">Ask assistant</div>
            <div className="truncate text-xs text-gray-500">
              {thread ? `About: ${thread.subject}` : "No thread selected"}
            </div>
          </div>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4 text-[13px]">
          {messages.length === 0 && (
            <p className="text-gray-500">
              Ask anything about this thread. Answers come from the
              conversation, the linked booking and the knowledge base – nothing
              is sent or changed.
            </p>
          )}
          {messages.map((m, i) => (
            <div key={i} className={cn(m.role === "you" && "flex justify-end")}>
              <div
                className={cn(
                  "max-w-[90%] rounded-md px-3 py-2 leading-relaxed",
                  m.role === "you"
                    ? "bg-primary text-white"
                    : "w-fit bg-gray-100 text-gray-900",
                )}
              >
                {m.text}
              </div>
            </div>
          ))}
          {thinking && (
            <div className="w-fit rounded-md bg-gray-100 px-3 py-2 text-gray-400">
              Thinking…
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 px-4 py-3">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {prompts.map((p) => (
              <button
                key={p}
                onClick={() => ask(p)}
                className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:border-primary hover:text-primary"
              >
                {p}
              </button>
            ))}
          </div>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              ask(input);
            }}
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about this thread…"
              className="h-9 text-[13px]"
            />
            <Button
              type="submit"
              size="sm"
              disabled={!input.trim() || thinking}
              className="h-9 bg-primary hover:bg-primary/90"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ---------- new mail ---------- */

type NewMail = {
  mailboxId: string;
  sendAs: string;
  to: string;
  subject: string;
  /** What I wrote, as plain text. */
  body: string;
  /** The whole email from the editor, signature included. */
  html: string;
  attachments: File[];
};

function NewMailDialog({
  open,
  onOpenChange,
  onSend,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolves true once sent, so the form clears only then. */
  onSend: (mail: NewMail) => Promise<boolean>;
}) {
  const mailboxes = useMailboxes();
  const { staff, me, live, signatureHtml } = usePeople();
  const synced = mailboxes.filter((m) => m.synced);
  const [mailboxId, setMailboxId] = useState(synced[0]?.id ?? "");
  const [sendAs, setSendAs] = useState(me.id);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const blank = () => mailDraft({ signatureHtml });
  const [body, setBody] = useState<MailDraft>(blank);
  const [attachments, setAttachments] = useState<File[]>([]);
  const mailbox = mailboxes.find((m) => m.id === mailboxId);
  const canSend = to.trim() !== "" && subject.trim() !== "" && !sending;

  const reset = () => {
    setTo("");
    setSubject("");
    setBody(blank());
    setAttachments([]);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg gap-0 p-0">
        <DialogHeader className="space-y-1 px-5 pt-5">
          <DialogTitle className="text-base">New mail</DialogTitle>
          <DialogDescription className="text-[13px]">
            {live
              ? `Starts a new thread from ${mailbox?.address ?? "the shared inbox"}.`
              : `Starts a new thread in the ${mailbox?.city ?? "shared"} inbox — it files under Needs filing until a category is set.`}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4 px-5 py-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!canSend) return;
            setSending(true);
            const sent = await onSend({
              mailboxId,
              sendAs,
              to: to.trim(),
              subject: subject.trim(),
              body: body.text,
              html: body.html,
              attachments,
            });
            setSending(false);
            if (sent) reset();
          }}
        >
          <div className={cn("grid gap-3", !live && "grid-cols-2")}>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">From</Label>
              <Select value={mailboxId} onValueChange={setMailboxId}>
                <SelectTrigger className="h-9 text-[13px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {synced.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-[13px]">
                      {m.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!live && (
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Send as</Label>
                <Select value={sendAs} onValueChange={setSendAs}>
                  <SelectTrigger className="h-9 text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        className="text-[13px]"
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-mail-to" className="text-xs text-gray-600">
              To <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-mail-to"
              required
              placeholder="name@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 text-[13px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-mail-subject" className="text-xs text-gray-600">
              Subject <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-mail-subject"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-9 text-[13px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Message</Label>
            <div className="max-h-72 overflow-y-auto rounded-md border border-input">
              <MailEditor
                value={body.html}
                onChange={setBody}
                className="min-h-[160px] px-3 py-2 text-[13px]"
              />
            </div>
            <AttachmentList
              files={attachments}
              onChange={setAttachments}
              disabled={sending}
            />
          </div>
          <DialogFooter className="-mx-5 -mb-4 border-t border-gray-200 bg-gray-50 px-5 py-3">
            {live && (
              <AttachButton
                files={attachments}
                onChange={setAttachments}
                disabled={sending}
                className="mr-auto"
              />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!canSend}
              className="bg-primary hover:bg-primary/90"
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- thread view ---------- */

/** The customer wrote last in an open thread of a kind that gets a reply. */
function awaitsReply(t: Thread): boolean {
  return (
    !t.done &&
    t.lastDirection === "in" &&
    (t.gmailFolder ?? "inbox") === "inbox" &&
    DRAFTED_CATEGORIES.includes(t.category)
  );
}

/**
 * A suggested action staff have not decided yet. Decided cards stay in the
 * thread, but no longer count as something to do.
 */
const openAction = (t: Thread) => Boolean(t.action && !t.action.decided);

/** Categories the drafter writes replies for; see inbox_draft_queue. */
const DRAFTED_CATEGORIES: Category[] = [
  "enquiry",
  "change",
  "cancellation",
  "invoice",
];

/**
 * A booking change the assistant suggests. Nothing changes until staff apply
 * it; live, Apply goes through the same staff booking endpoint as the
 * booking page, so emails, reminders and the activity log follow as usual.
 */
function SuggestedActionCard({
  action,
  booking,
  state,
  onDecided,
}: {
  action: SuggestedAction;
  booking?: LinkedBooking;
  state?: "applied" | "rejected";
  onDecided: (s: "applied" | "rejected") => void;
}) {
  const { live } = usePeople();
  const router = useRouter();
  const bookingHref = useBookingHref(booking);
  const [busy, setBusy] = useState<"applied" | "rejected" | null>(null);
  const stale = Boolean(action.stale) && !state;

  const decide = async (decision: "applied" | "rejected") => {
    if (!live) return onDecided(decision);
    setBusy(decision);
    try {
      if (decision === "applied") {
        const prepared = await prepareSuggestedAction(action.id);
        if (!prepared.success || !prepared.data) {
          throw new Error(prepared.error ?? "Could not apply the change");
        }
        const { venueId, bookingId, body } = prepared.data;
        const response = await fetch(
          `/api/staff/venues/${venueId}/bookings/${bookingId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(
            data.error || data.details || "Could not update the booking",
          );
        }
      }
      const result = await decideSuggestedAction(action.id, decision);
      // The booking may already be changed; say so rather than hide it.
      if (!result.success) {
        throw new Error(result.error ?? "Could not record the decision");
      }
      onDecided(decision);
      if (decision === "applied") toast.success("Booking updated");
      router.refresh();
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-4">
      <div className={cn(mono, "mb-2 flex items-center gap-2 text-gray-500")}>
        <Sparkles className="h-3 w-3" /> Assistant suggests 1 action
        <span className="normal-case italic tracking-normal text-gray-400">
          — nothing changes until you apply it
        </span>
      </div>
      <div
        className={cn(
          "rounded border",
          state === "applied"
            ? "border-emerald-200"
            : state === "rejected" || stale
              ? "border-gray-200 opacity-60"
              : "border-sky-200",
        )}
      >
        <div className="flex items-center gap-2 border-b border-sky-200 bg-sky-50/60 px-4 py-2">
          <RefreshCw className="h-3.5 w-3.5 text-gray-500" />
          <span className={cn(mono, "text-gray-700")}>{action.field}</span>
          {stale && (
            <span className="ml-auto">
              <Chip tone="neutral">no longer applies</Chip>
            </span>
          )}
        </div>
        <div className="border-b border-gray-100 px-4 py-2 text-xs italic text-gray-500">
          Because: {action.reason}
        </div>
        <div className="px-4 py-3">
          <div className="font-medium">{action.title}</div>
          <div className="mt-2 flex items-center gap-2">
            <span className={cn(mono, "text-gray-400")}>{action.field}</span>
            <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 line-through">
              {action.from}
            </span>
            <span className="text-gray-400">→</span>
            <span className="rounded border border-sky-300 bg-sky-50 px-2 py-0.5 font-medium text-sky-800">
              {action.to}
            </span>
            {booking &&
              (bookingHref ? (
                <Link
                  href={bookingHref}
                  className="ml-auto text-xs text-gray-400 hover:text-primary hover:underline"
                >
                  {booking.reference} · {booking.show}
                </Link>
              ) : (
                <span className="ml-auto text-xs text-gray-400">
                  {booking.reference} · {booking.show}
                </span>
              ))}
          </div>
          {stale && booking && (
            <p className="mt-2 text-xs text-gray-500">
              The booking is now {booking.status}, so this change can no longer
              be applied.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 border-t border-gray-200 bg-gray-50/70 px-4 py-2">
          {state === "applied" ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
              <Check className="h-3.5 w-3.5" /> Updated · logged in booking
              activity
            </span>
          ) : state === "rejected" ? (
            <span className="text-xs text-gray-500">
              Declined — nothing was changed.
            </span>
          ) : (
            <>
              {!stale && (
                <Button
                  size="sm"
                  disabled={busy !== null}
                  onClick={() => decide("applied")}
                  className="h-7 gap-1.5 bg-primary text-xs hover:bg-primary/90"
                >
                  {busy === "applied" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                  Update booking
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={busy !== null}
                onClick={() => decide("rejected")}
                className="ml-auto h-7 gap-1.5 text-xs"
              >
                {busy === "rejected" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
                Decline
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The changes a change email asks for. Nothing is applied from here: staff
 * make the change on the booking page, where price and availability are
 * checked as usual, then mark it done (or dismiss it).
 */
function ChangeRequestCard({
  action,
  booking,
  state,
  onDecided,
}: {
  action: SuggestedAction;
  booking?: LinkedBooking;
  state?: "applied" | "rejected";
  onDecided: (s: "applied" | "rejected") => void;
}) {
  const { live } = usePeople();
  const router = useRouter();
  const cartHref = useCartHref(booking);
  const [busy, setBusy] = useState<"applied" | "rejected" | null>(null);
  const stale = Boolean(action.stale) && !state;
  const changes = action.changes ?? [];
  // Update applies the number of people itself; the rest (add-ons, date,
  // time, show...) are changed in the cart.
  const participantsLeft = changes.some(
    (c) => c.field === "participants" && !c.done,
  );
  const cartLeft = changes.some((c) => c.field !== "participants" && !c.done);

  const update = () => {
    if (participantsLeft) return applyParticipants();
    if (cartLeft) {
      if (cartHref) window.open(cartHref, "_blank", "noopener");
      return;
    }
    // Everything asked for is already on the booking.
    return decide("applied");
  };

  const decide = async (decision: "applied" | "rejected") => {
    if (!live) return onDecided(decision);
    setBusy(decision);
    try {
      const result = await decideSuggestedAction(action.id, decision);
      if (!result.success) {
        throw new Error(result.error ?? "Could not record the decision");
      }
      onDecided(decision);
      router.refresh();
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  };

  // The new number of people through the endpoint the cart's Modify booking
  // uses, so price, per-person add-ons, emails and the activity log follow.
  const applyParticipants = async () => {
    if (!live) return;
    setBusy("applied");
    try {
      const prepared = await prepareParticipantChange(action.id);
      if (!prepared.success || !prepared.data) {
        throw new Error(prepared.error ?? "Could not change the booking");
      }
      const { venueId, bookingId, body } = prepared.data;
      const response = await fetch(
        `/api/staff/venues/${venueId}/bookings/${bookingId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.error || data.details || "Could not update the booking",
        );
      }
      toast.success(`Booking updated: ${body.participants} participants`);
      // With changes left for the cart, the card stays open for them.
      if (!cartLeft) {
        const result = await decideSuggestedAction(action.id, "applied");
        if (result.success) onDecided("applied");
      }
      router.refresh();
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-4">
      <div className={cn(mono, "mb-2 flex items-center gap-2 text-gray-500")}>
        <Sparkles className="h-3 w-3" /> Assistant suggests 1 action
        <span className="normal-case italic tracking-normal text-gray-400">
          — nothing changes until you update the booking
        </span>
      </div>
      <div
        className={cn(
          "rounded border",
          state === "applied"
            ? "border-emerald-200"
            : state === "rejected" || stale
              ? "border-gray-200 opacity-60"
              : "border-sky-200",
        )}
      >
        <div className="flex items-center gap-2 border-b border-sky-200 bg-sky-50/60 px-4 py-2">
          <ListChecks className="h-3.5 w-3.5 text-gray-500" />
          <span className={cn(mono, "text-gray-700")}>{action.field}</span>
          {stale && (
            <span className="ml-auto">
              <Chip tone="neutral">already on the booking</Chip>
            </span>
          )}
        </div>
        <div className="border-b border-gray-100 px-4 py-2 text-xs italic text-gray-500">
          Because: {action.reason}
        </div>
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="font-medium">{action.title}</span>
            {booking && (
              <span className="ml-auto text-xs text-gray-400">
                {booking.reference} · {booking.show}
              </span>
            )}
          </div>
          <ul className="mt-2 space-y-1.5">
            {changes.map((change, index) => (
              <li key={index} className="flex items-center gap-2">
                <span className={cn(mono, "min-w-28 text-gray-400")}>
                  {change.label}
                </span>
                <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 line-through">
                  {change.from ?? "?"}
                </span>
                <span className="text-gray-400">→</span>
                <span className="rounded border border-sky-300 bg-sky-50 px-2 py-0.5 font-medium text-sky-800">
                  {change.to}
                </span>
                {change.done ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-700">
                    <Check className="h-3 w-3" /> on the booking
                  </span>
                ) : (
                  change.field !== "participants" &&
                  !state &&
                  cartHref && (
                    <Link
                      href={cartHref}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" /> change in cart
                    </Link>
                  )
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-2 border-t border-gray-200 bg-gray-50/70 px-4 py-2">
          {state === "applied" ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
              <Check className="h-3.5 w-3.5" /> Updated
            </span>
          ) : state === "rejected" ? (
            <span className="text-xs text-gray-500">
              Declined — nothing was changed.
            </span>
          ) : (
            <>
              <Button
                size="sm"
                disabled={busy !== null || (!participantsLeft && cartLeft && !cartHref)}
                onClick={update}
                className="h-7 gap-1.5 bg-primary text-xs hover:bg-primary/90"
              >
                {busy === "applied" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : !participantsLeft && cartLeft ? (
                  <ExternalLink className="h-3.5 w-3.5" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                {!participantsLeft && cartLeft
                  ? "Update in cart"
                  : "Update booking"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy !== null}
                onClick={() => decide("rejected")}
                className="ml-auto h-7 gap-1.5 text-xs"
              >
                {busy === "rejected" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
                Decline
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadView({
  thread,
  aiAutomatic,
  autoDraft,
  setAutoDraft,
  actionState,
  onAction,
  composeMode,
  setComposeMode,
  savedToKb,
  onSaveToKb,
  onAssign,
  composeRequest,
  onDraft,
  onAsk,
}: {
  thread: Thread;
  /** Off: no draft is written on open, so offer one here instead. */
  aiAutomatic: boolean;
  autoDraft: boolean;
  setAutoDraft: (b: boolean) => void;
  actionState?: "applied" | "rejected";
  onAction: (s: "applied" | "rejected") => void;
  composeMode: "reply" | "note";
  setComposeMode: (m: "reply" | "note") => void;
  savedToKb: boolean;
  onSaveToKb: () => void;
  onAssign: (id: string) => void;
  composeRequest: ComposeRequest | null;
  /** A suggested reply written on request replaces the thread's. */
  onDraft: (draft: SuggestedDraft | undefined) => void;
  /** Opens the Ask assistant panel for this thread. */
  onAsk: () => void;
}) {
  const mailboxes = useMailboxes();
  const router = useRouter();
  const { staff, me, live, signatureHtml } = usePeople();
  const bookingHref = useBookingHref(thread.booking);
  const mailbox = mailboxes.find((m) => m.id === thread.mailboxId);
  const assignee = staff.find((s) => s.id === thread.assignee);
  const [assignOpen, setAssignOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyKind, setReplyKind] = useState<ReplyKind>("reply");
  const [showCcBcc, setShowCcBcc] = useState(false);
  /** A fresh draft: what to start with, then my signature. */
  const blankDraft = (written = "") => mailDraft({ written, signatureHtml });
  const [draft, setDraft] = useState<MailDraft>(() => blankDraft());
  const [attachments, setAttachments] = useState<File[]>([]);
  /** A forward: the forwarded email and those of its files to send along. */
  const [forwarding, setForwarding] = useState<{
    messageId: string;
    files: NonNullable<Thread["messages"][number]["attachments"]>;
  } | null>(null);
  const [to, setTo] = useState(thread.customer.email);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState(`Re: ${thread.subject}`);
  const [note, setNote] = useState("");
  const [sending, startSending] = useTransition();
  const canSend =
    !sending &&
    (Boolean(draft.text.trim()) ||
      attachments.length > 0 ||
      replyKind === "forward");
  const loading = live && thread.messages.length === 0 && thread.msgCount > 0;
  /** Older emails start folded to one line; the latest and drafts stay open. */
  const latestEmailId = [...thread.messages].reverse().find((m) => !m.internal)
    ?.id;
  const [openedEmails, setOpenedEmails] = useState<Set<string>>(new Set());
  const isOpen = (m: Thread["messages"][number]) =>
    m.id === latestEmailId || m.draft || openedEmails.has(m.id);

  /**
   * HTML of the open emails, fetched once each as they open: with inline
   * images one can be megabytes, so the thread does not load them all.
   * Until it arrives, and for an email without any, the text is shown.
   */
  const [htmlOf, setHtmlOf] = useState<Record<string, string | null>>({});
  const htmlRequested = useRef(new Set<string>());
  useEffect(() => {
    if (!live) return;
    for (const m of thread.messages) {
      if (m.internal || !isOpen(m) || htmlRequested.current.has(m.id)) {
        continue;
      }
      htmlRequested.current.add(m.id);
      getMessageHtml(thread.id, m.id).then((result) => {
        if (result.success) {
          setHtmlOf((prev) => ({ ...prev, [m.id]: result.data ?? null }));
        } else {
          // Retried on the next refresh or open; the text stays meanwhile.
          htmlRequested.current.delete(m.id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, thread.id, thread.messages, latestEmailId, openedEmails]);
  const htmlFor = (m: Thread["messages"][number]) => m.html ?? htmlOf[m.id];

  /** Everyone else on the latest email, for Reply all. */
  const otherRecipients = () => {
    const last = [...thread.messages].reverse().find((m) => !m.internal);
    if (!last) return "";
    const skip = new Set(
      [mailbox?.address, thread.customer.email].map((a) => a?.toLowerCase()),
    );
    return splitAddresses([last.to, last.cc ?? ""].join(","))
      .filter((address) => !skip.has(address.toLowerCase()))
      .join(", ");
  };

  const send = () =>
    startSending(async () => {
      const input = {
        conversationId: thread.id,
        to: splitAddresses(to),
        cc: splitAddresses(cc),
        bcc: splitAddresses(bcc),
        subject,
        html: draft.html,
        forwardAttachments:
          replyKind === "forward" && forwarding?.files.length
            ? {
                messageId: forwarding.messageId,
                partIds: forwarding.files.map((f) => f.id),
              }
            : undefined,
        // The AI draft on screen, kept next to what was actually sent.
        aiDraft:
          replyKind !== "forward" && thread.draft?.id
            ? { id: thread.draft.id, used: copiedDraftId === thread.draft.id }
            : undefined,
      };
      const result = attachments.length
        ? await sendWithAttachments("reply", input, attachments)
        : await sendReply(input);
      if (!result.success) {
        toast.error(result.error ?? "Could not send");
        return;
      }
      toast.success("Sent");
      setDraft(blankDraft());
      setCopiedDraftId(null);
      setAttachments([]);
      setForwarding(null);
      setComposeOpen(false);
      router.refresh();
    });

  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [mentionCleared, setMentionCleared] = useState(false);

  const clearMention = () =>
    startSending(async () => {
      const result = await clearMentionForMe(thread.id);
      if (!result.success) {
        toast.error(result.error ?? "Could not clear the mention");
        return;
      }
      setMentionCleared(true);
      router.refresh();
    });

  const saveNote = () =>
    startSending(async () => {
      const result = await addInternalNote(
        thread.id,
        note,
        mentionedIn(note, staff),
      );
      if (!result.success) {
        toast.error(result.error ?? "Could not add the note");
        return;
      }
      setNote("");
      setComposeOpen(false);
      router.refresh();
    });
  const openCompose = (mode: "reply" | "note") => {
    setComposeMode(mode);
    setComposeOpen(true);
  };
  // Forwarding quotes the last email under my signature, Gmail-style, unless
  // I have started writing.
  const forwardedEmail = () => {
    const last = [...thread.messages].reverse().find((m) => !m.internal);
    if (!last) return "";
    return [
      "---------- Forwarded message ---------",
      `From: ${last.from.name} <${last.from.email}>`,
      `Date: ${last.sentIso ? formatFullDate(new Date(last.sentIso)) : last.sentAt}`,
      `Subject: ${thread.subject}`,
      `To: ${last.to}`,
      "",
      // Paragraphs keep the blank line between them (and any extra ones the
      // sender typed, which toParagraphs keeps at a paragraph's start).
      last.body.join("\n\n"),
    ].join("\n");
  };
  const chooseKind = (kind: ReplyKind) => {
    setReplyKind(kind);
    setTo(kind === "forward" ? "" : thread.customer.email);
    setSubject(`${kind === "forward" ? "Fwd" : "Re"}: ${thread.subject}`);
    const others = kind === "reply-all" ? otherRecipients() : "";
    setCc(others);
    if (others) setShowCcBcc(true);
    // Forwarding sends the email's files along, as Gmail does.
    const last = [...thread.messages].reverse().find((m) => !m.internal);
    setForwarding(
      kind === "forward" && last?.attachments?.length
        ? { messageId: last.id, files: last.attachments }
        : null,
    );
    if (draft.text.trim()) return;
    setDraft(
      kind === "forward"
        ? mailDraft({ signatureHtml, forwarded: forwardedEmail() })
        : blankDraft(),
    );
  };
  // Toolbar Reply / Reply all / Forward open the composer in that mode -
  // only for the thread they were clicked on.
  useEffect(() => {
    if (!composeRequest || composeRequest.threadId !== thread.id) return;
    chooseKind(composeRequest.kind);
    setComposeMode("reply");
    setComposeOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeRequest]);
  const [draftCollapsed, setDraftCollapsed] = useState(false);
  // The message "Add to FAQ" was opened on; null while closed.
  const [faqMessageId, setFaqMessageId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // The AI draft last copied into the composer.
  const [copiedDraftId, setCopiedDraftId] = useState<string | null>(null);
  const [drafting, startDrafting] = useTransition();

  const suggestReply = () =>
    startDrafting(async () => {
      const result = await regenerateDraft(thread.id);
      if (!result.success || !result.data) {
        toast.error(result.error ?? "Could not write a draft");
        return;
      }
      onDraft(result.data);
      setAutoDraft(true);
      setDraftCollapsed(false);
      setCopied(false);
    });

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col print:block">
      {/* header */}
      <div className="flex items-start gap-3 border-b border-gray-200 px-6 py-4">
        <Avatar initials={initialsOf(thread.customer.name)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">
              {thread.customer.name}
            </span>
            <span className="text-gray-400">·</span>
            <span className="truncate text-gray-600">{thread.subject}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Chip tone={categoryTone(thread.category)}>
              {CATEGORY_LABELS[thread.category]}{" "}
              <ChevronDown className="h-2.5 w-2.5" />
            </Chip>
            {thread.booking && (
              <Chip tone="green">{thread.booking.status}</Chip>
            )}
            {thread.needsFiling && <Chip tone="amber">Needs booking</Chip>}
            {thread.markedForReview && (
              <Chip tone="violet">
                <ClipboardCheck className="h-3 w-3" /> For review
              </Chip>
            )}
            {live && thread.mentionedMe && !mentionCleared && (
              <span className="inline-flex items-center gap-1.5">
                <Chip tone="blue">
                  <AtSign className="h-3 w-3" /> Mentioned you
                </Chip>
                <button
                  onClick={clearMention}
                  disabled={sending}
                  title="Remove from your Mentions until someone mentions you here again"
                  className="rounded px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  <Check className="mr-1 inline h-3 w-3" />
                  Done for me
                </button>
              </span>
            )}
            <span className={cn(mono, "text-gray-400")}>
              {mailbox?.address} · {thread.msgCount} msgs ·{" "}
              {thread.booking ? (
                bookingHref ? (
                  <Link
                    href={bookingHref}
                    className="text-gray-600 hover:text-primary hover:underline"
                  >
                    {thread.booking.reference} →
                  </Link>
                ) : (
                  <span className="text-gray-600">
                    {thread.booking.reference}
                  </span>
                )
              ) : (
                <span>no record yet</span>
              )}
            </span>
          </div>
        </div>
        <div className="relative flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAssignOpen((o) => !o)}
              className="flex items-center gap-2 rounded border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50"
            >
              {assignee ? (
                <>
                  <Avatar initials={assignee.initials} size="sm" />
                  {assignee.id === me.id ? "Assigned to me" : assignee.name}
                </>
              ) : (
                <>
                  <UserPlus className="h-3.5 w-3.5" /> Unassigned
                </>
              )}
              <ChevronDown className="h-3 w-3 text-gray-400" />
            </button>
            <Button
              size="sm"
              className="h-7 gap-1.5 bg-primary text-xs hover:bg-primary/90"
              onClick={onAsk}
            >
              <Sparkles className="h-3.5 w-3.5" /> Ask assistant
            </Button>
          </div>
          {assignOpen && (
            <div className="absolute left-0 top-8 z-10 max-h-80 w-52 overflow-y-auto rounded border border-gray-200 bg-white p-1 shadow-md">
              {staff.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    onAssign(s.id);
                    setAssignOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-gray-100"
                >
                  <Avatar initials={s.initials} size="sm" />
                  <span className="flex-1">{s.name}</span>
                  <span className="text-[10px] text-gray-400">{s.days}</span>
                </button>
              ))}
            </div>
          )}
          <label
            className={cn(
              mono,
              "flex items-center gap-2 text-gray-500",
              live && "hidden",
            )}
          >
            Auto-draft
            <button
              onClick={() => setAutoDraft(!autoDraft)}
              className={cn(
                "relative h-4 w-7 rounded-full transition-colors",
                autoDraft ? "bg-primary" : "bg-gray-300",
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all",
                  autoDraft ? "left-3.5" : "left-0.5",
                )}
              />
            </button>
          </label>
        </div>
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {!live && (
          <div
            className={cn(mono, "mb-3 flex items-center gap-3 text-gray-400")}
          >
            <span className="h-px flex-1 bg-gray-200" /> Today{" "}
            <span className="h-px flex-1 bg-gray-200" />
          </div>
        )}

        {loading && (
          <div className="py-6 text-center text-xs text-gray-400">
            Loading conversation…
          </div>
        )}

        {thread.messages.map((m) =>
          m.internal ? (
            <div
              key={m.id}
              className="mb-4 rounded border border-amber-200 bg-amber-50/60 px-4 py-3"
            >
              <div
                className={cn(
                  mono,
                  "mb-1 flex items-center gap-2 text-amber-800",
                )}
              >
                <MessageSquare className="h-3 w-3" /> Internal note · {m.author}{" "}
                · <LocalTime iso={m.sentIso} fallback={m.sentAt} kind="message" />
              </div>
              {/* One block, as typed: every line break and blank line kept. */}
              <p className="whitespace-pre-line text-gray-800">
                {live
                  ? withMentions(m.body.join("\n\n"), staff)
                  : m.body.join("\n\n")}
              </p>
            </div>
          ) : !isOpen(m) ? (
            <button
              key={m.id}
              onClick={() => setOpenedEmails((s) => new Set(s).add(m.id))}
              aria-expanded={false}
              title="Show this email"
              className="mb-4 flex w-full items-center gap-3 rounded border border-gray-200 bg-gray-50/70 px-4 py-2 text-left hover:bg-gray-100"
            >
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              <span className="shrink-0 font-medium">{m.from.name}</span>
              <span className="min-w-0 flex-1 truncate text-gray-500">
                {m.body.join(" ")}
              </span>
              {m.attachments && (
                <Paperclip className="h-3 w-3 shrink-0 text-gray-400" />
              )}
              <span className="shrink-0 text-xs text-gray-500">
                <LocalTime iso={m.sentIso} fallback={m.sentAt} kind="message" />
              </span>
            </button>
          ) : (
            <div key={m.id} className="mb-4 rounded border border-gray-200">
              <div className="grid grid-cols-[60px_1fr_auto] gap-x-3 gap-y-1 border-b border-gray-200 bg-gray-50/70 px-4 py-3">
                <span className={cn(mono, "text-gray-400")}>From</span>
                <span>
                  {m.from.name}{" "}
                  <span className="text-gray-500">&lt;{m.from.email}&gt;</span>
                </span>
                <span className="row-span-3 flex items-start gap-2">
                  {m.draft ? (
                    <Chip tone="amber">
                      <FileText className="h-3 w-3" /> Draft
                    </Chip>
                  ) : (
                    <Chip tone="blue">
                      <Mail className="h-3 w-3" /> Email
                    </Chip>
                  )}
                  <span className="text-xs text-gray-500">
                    <LocalTime
                      iso={m.sentIso}
                      fallback={m.sentAt}
                      kind="message"
                    />
                  </span>
                  {/* Enquiries are where customers ask what a FAQ answers. */}
                  {live && !m.draft && thread.category === "enquiry" && (
                    <button
                      onClick={() => setFaqMessageId(m.id)}
                      className="flex items-center gap-1 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      title="Turn this question and its answer into a public FAQ"
                    >
                      <BookPlus className="h-3 w-3" /> Add to FAQ
                    </button>
                  )}
                  {/* Folds an older email back; the latest and drafts stay open. */}
                  {openedEmails.has(m.id) &&
                    m.id !== latestEmailId &&
                    !m.draft && (
                      <button
                        onClick={() =>
                          setOpenedEmails((s) => {
                            const next = new Set(s);
                            next.delete(m.id);
                            return next;
                          })
                        }
                        aria-expanded={true}
                        title="Hide this email"
                        className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    )}
                </span>
                <span className={cn(mono, "text-gray-400")}>To</span>
                <span className="text-gray-700">{m.to}</span>
                {m.cc && (
                  <>
                    <span className={cn(mono, "text-gray-400")}>Cc</span>
                    <span className="text-gray-700">{m.cc}</span>
                  </>
                )}
                <span className={cn(mono, "text-gray-400")}>Subject</span>
                <span className="text-gray-700">{thread.subject}</span>
              </div>
              {htmlFor(m) ? (
                <div className="px-4 py-4">
                  <EmailHtmlFrame html={htmlFor(m)!} />
                </div>
              ) : (
                <div className="space-y-3 px-4 py-4">
                  {m.body.map((p, i) => (
                    <p key={i} className="whitespace-pre-line leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              )}
              {m.attachments && (
                <ul className="flex flex-wrap gap-1.5 border-t border-gray-100 px-4 py-3">
                  {m.attachments.map((file) => (
                    <li
                      key={file.url}
                      className="flex max-w-[300px] items-center gap-1.5 rounded border border-gray-200 bg-white text-xs text-gray-700"
                    >
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex min-w-0 items-center gap-1.5 py-1 pl-2 hover:underline"
                        title={`Open ${file.name}`}
                      >
                        <Paperclip className="h-3 w-3 shrink-0 text-gray-400" />
                        <span className="truncate">{file.name}</span>
                        <span className="shrink-0 text-gray-400">
                          {formatFileSize(file.size)}
                        </span>
                      </a>
                      <a
                        href={`${file.url}?download=1`}
                        download={file.name}
                        className="mr-1 shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        title={`Download ${file.name}`}
                      >
                        <Download className="h-3 w-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ),
        )}

        {live && (
          <AddToFaqDialog
            conversationId={thread.id}
            messageId={faqMessageId}
            onClose={() => setFaqMessageId(null)}
          />
        )}

        {/* suggested action */}
        {thread.action &&
          (thread.action.kind === "change" ? (
            <ChangeRequestCard
              action={thread.action}
              booking={thread.action.booking ?? thread.booking}
              state={actionState ?? thread.action.decided}
              onDecided={onAction}
            />
          ) : (
            <SuggestedActionCard
              action={thread.action}
              booking={thread.action.booking ?? thread.booking}
              state={actionState ?? thread.action.decided}
              onDecided={onAction}
            />
          ))}

        {/* suggested draft */}
        {live &&
          !aiAutomatic &&
          !thread.draft &&
          !thread.draftWriting &&
          !thread.done &&
          thread.lastDirection === "in" && (
            <div className="mb-4 flex items-center gap-3 rounded-md border border-dashed border-primary/40 px-4 py-3 text-sm text-gray-600">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="flex-1">
                The assistant can suggest a reply to this email.
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-xs"
                disabled={drafting}
                onClick={suggestReply}
              >
                {drafting ? "Writing…" : "Suggest reply"}
              </Button>
            </div>
          )}
        {thread.draftWriting && !thread.draft && autoDraft && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-primary/30 border-l-4 border-l-primary bg-primary/[0.03] px-4 py-3 text-sm text-primary">
            <Sparkles className="h-3.5 w-3.5 animate-pulse" />
            Assistant is writing a suggested reply…
          </div>
        )}
        {thread.draft && autoDraft && (
          <div className="mb-4">
            <div
              className={cn(mono, "mb-2 flex items-center gap-2 text-primary")}
            >
              <Sparkles className="h-3 w-3" /> Assistant suggests
              <span className="normal-case italic tracking-normal text-gray-400">
                — nothing is sent until you send it from the composer
              </span>
            </div>
            <div className="rounded-md border border-primary/30 border-l-4 border-l-primary bg-primary/[0.03] shadow-sm">
              <div className="flex items-center gap-2 border-b border-primary/20 bg-primary/10 px-4 py-2">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <span className={cn(mono, "font-semibold text-primary")}>
                  Suggested draft · sent as {thread.draft.sendAs}
                </span>
                <span className={cn(mono, "ml-auto italic text-primary/70")}>
                  {thread.draft.label}
                </span>
                <button
                  onClick={() => setDraftCollapsed((c) => !c)}
                  className="rounded p-1 text-primary/70 hover:bg-primary/15 hover:text-primary"
                  title={draftCollapsed ? "Expand" : "Collapse"}
                  aria-expanded={!draftCollapsed}
                >
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform",
                      draftCollapsed && "-rotate-90",
                    )}
                  />
                </button>
              </div>
              {draftCollapsed ? (
                <button
                  onClick={() => setDraftCollapsed(false)}
                  className="w-full truncate px-4 py-2 text-left text-xs italic text-gray-500 hover:bg-gray-50"
                >
                  {thread.draft.paragraphs[0]}
                </button>
              ) : (
                <>
                  <div className="space-y-3 px-4 py-4 leading-relaxed">
                    {thread.draft.paragraphs.map((p, i) => (
                      <p key={i} className="whitespace-pre-line">
                        {p}
                      </p>
                    ))}
                  </div>
                  {(thread.draft.note || thread.draft.error) && (
                    <div className="space-y-1 border-t border-amber-200 bg-amber-50/70 px-4 py-2 text-xs text-amber-900">
                      {thread.draft.error && <p>{thread.draft.error}</p>}
                      {thread.draft.note && (
                        <p>
                          <span className="font-semibold">Check before sending: </span>
                          {thread.draft.note}
                        </p>
                      )}
                    </div>
                  )}
                  {thread.draft.sources.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 border-t border-primary/15 px-4 py-2">
                      <span className={cn(mono, "mr-1 text-gray-400")}>
                        Based on
                      </span>
                      {thread.draft.sources.map((s) => (
                        <Chip key={s} tone="neutral">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 border-t border-primary/15 bg-white/70 px-4 py-2">
                    <Button
                      size="sm"
                      variant={copied ? "outline" : "default"}
                      disabled={thread.draft.paragraphs.length === 0}
                      className={cn(
                        "h-7 gap-1.5 text-xs",
                        copied
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-50"
                          : "bg-primary hover:bg-primary/90",
                      )}
                      onClick={() => {
                        setDraft(
                          blankDraft(thread.draft!.paragraphs.join("\n\n")),
                        );
                        setCopied(true);
                        setCopiedDraftId(thread.draft!.id ?? null);
                        openCompose("reply");
                      }}
                    >
                      {copied ? (
                        <>
                          <Check className="h-3.5 w-3.5" /> Copied — edit below
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" /> Copy to composer
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1.5 text-xs"
                      disabled={drafting}
                      onClick={live ? suggestReply : undefined}
                    >
                      <RefreshCw
                        className={cn("h-3.5 w-3.5", drafting && "animate-spin")}
                      />
                      {drafting ? "Writing…" : "Regenerate"}
                    </Button>
                    <AiFeedback
                      key={thread.draft.paragraphs.join("\n\n")}
                      live={live}
                      className={live ? "ml-auto" : undefined}
                      input={{
                        conversationId: thread.id,
                        target: "draft",
                        output:
                          thread.draft.paragraphs.join("\n\n") ||
                          thread.draft.error ||
                          "(empty draft)",
                      }}
                    />
                    {/* Wireframe only: nothing saves to the knowledge base yet. */}
                    {!live && (
                      <button
                        onClick={onSaveToKb}
                        className={cn(
                          "ml-auto flex items-center gap-1.5 rounded px-2 py-1 text-xs",
                          savedToKb
                            ? "text-emerald-700"
                            : "text-gray-600 hover:bg-gray-100",
                        )}
                      >
                        <BookmarkPlus className="h-3.5 w-3.5" />
                        {savedToKb
                          ? "Saved to knowledge base"
                          : "Save answer to knowledge base"}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {thread.needsFiling && (
          <div className="mb-4 rounded border border-amber-200 bg-amber-50/60 px-4 py-3">
            <div className={cn(mono, "mb-1 text-amber-800")}>
              Assistant · needs your input
            </div>
            <p className="text-gray-800">
              {live
                ? "This looks like it's about an existing booking, but I couldn't tell which one. Ask the assistant to look up the customer's bookings."
                : "I couldn't match this email to a customer or booking. Link it to an existing customer, create a new enquiry, or tell me it's spam and I'll draft the reply from there."}
            </p>
            <div className={cn("mt-2 flex gap-2", live && "hidden")}>
              <Button size="sm" variant="outline" className="h-7 text-xs">
                Link to customer…
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs">
                Create enquiry
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs">
                Mark as spam
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* compose: a single bar until you click into it, like the reference.
          Open, it takes at most three quarters of the pane and the editor
          scrolls, so Send stays on screen however long the draft is. */}
      <div className="flex max-h-[75%] min-h-0 shrink-0 flex-col border-t border-gray-200 px-6 py-3">
        {!composeOpen ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => openCompose("reply")}
              className="flex h-9 flex-1 items-center gap-2 rounded border border-gray-200 bg-white px-3 text-left text-sm text-gray-500 hover:border-gray-300"
            >
              <PenSquare className="h-3.5 w-3.5" />
              {draft.text.trim() ? (
                <>
                  <span className="truncate text-gray-700">{draft.text}</span>
                  <span className={cn(mono, "ml-auto shrink-0 text-gray-400")}>
                    draft kept
                  </span>
                </>
              ) : (
                <>Reply to {thread.customer.email}…</>
              )}
            </button>
            <button
              onClick={() => openCompose("note")}
              className="flex h-9 items-center gap-1.5 rounded px-3 text-sm text-gray-700 hover:bg-gray-100"
            >
              <FileText className="h-3.5 w-3.5" /> Note
            </button>
          </div>
        ) : composeMode === "reply" ? (
          <div className="flex min-h-0 flex-col overflow-y-auto border border-gray-300 bg-white">
            {/* kind + audience */}
            <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2">
              <Sparkles className="h-3.5 w-3.5 text-gray-500" />
              <div className="flex overflow-hidden rounded border border-gray-300">
                {(
                  [
                    ["reply", "Reply"],
                    ["reply-all", "Reply all"],
                    ["forward", "Forward"],
                  ] as const
                ).map(([kind, label]) => (
                  <button
                    key={kind}
                    onClick={() => chooseKind(kind)}
                    className={cn(
                      mono,
                      "px-3 py-1.5",
                      replyKind === kind
                        ? "bg-white text-gray-900"
                        : "bg-gray-100 text-gray-500 hover:text-gray-900",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <span className={cn(mono, "ml-auto italic text-gray-500")}>
                Goes to customer
              </span>
              <button
                onClick={() => setComposeOpen(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                title="Collapse (draft is kept)"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            {/* headers */}
            <div className="grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-x-4 gap-y-2 border-b border-gray-200 px-4 py-3">
              <span className={cn(mono, "text-gray-500")}>From</span>
              <div className="flex items-center gap-2">
                <select
                  defaultValue={thread.mailboxId}
                  disabled={live}
                  title={
                    live ? "Replies go out from this thread's mailbox" : undefined
                  }
                  className="h-8 max-w-[15rem] rounded border border-gray-300 bg-white px-2 text-[13px] disabled:opacity-70"
                >
                  {mailboxes
                    .filter((m) => m.synced)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.address}
                      </option>
                    ))}
                </select>
                {!live && (
                  <>
                    <span className={cn(mono, "text-gray-500")}>as</span>
                    <select
                      defaultValue={me.id}
                      className="h-8 rounded border border-gray-300 bg-white px-2 text-[13px]"
                    >
                      {staff.map((st) => (
                        <option key={st.id} value={st.id}>
                          {st.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                {!showCcBcc && (
                  <button
                    onClick={() => setShowCcBcc(true)}
                    className={cn(
                      mono,
                      "ml-auto text-gray-500 hover:text-gray-900",
                    )}
                  >
                    + CC/BCC
                  </button>
                )}
              </div>
              <span className={cn(mono, "text-gray-500")}>To</span>
              <input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="name@example.com"
                className="h-8 w-full max-w-md rounded border border-gray-300 px-2 text-[13px]"
              />
              {showCcBcc && (
                <>
                  <span className={cn(mono, "text-gray-500")}>Cc</span>
                  <input
                    autoFocus
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="Separate several addresses with commas"
                    className="h-8 w-full max-w-md rounded border border-gray-300 px-2 text-[13px]"
                  />
                  <span className={cn(mono, "text-gray-500")}>Bcc</span>
                  <div className="flex max-w-md items-center gap-2">
                    <input
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      placeholder="Hidden from other recipients"
                      className="h-8 w-full rounded border border-gray-300 px-2 text-[13px]"
                    />
                    <button
                      onClick={() => setShowCcBcc(false)}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      title="Hide Cc/Bcc"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
              <span className={cn(mono, "text-gray-500")}>Subject</span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="h-8 w-full rounded border border-gray-300 px-2 text-[13px]"
              />
            </div>
            <div className="min-h-16 flex-auto overflow-y-auto">
              <MailEditor
                autoFocus
                value={draft.html}
                onChange={setDraft}
                onSubmit={() => {
                  if (live && canSend) send();
                }}
              />
            </div>
            {replyKind === "forward" && forwarding && (
              <AttachmentList
                files={forwarding.files}
                onChange={(files) => setForwarding({ ...forwarding, files })}
                disabled={sending}
                className="border-t border-gray-100 px-4 py-2"
              />
            )}
            <AttachmentList
              files={attachments}
              onChange={setAttachments}
              disabled={sending}
              className="border-t border-gray-100 px-4 py-2"
            />
            {/* footer */}
            <div className="flex items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2.5">
              <Button
                size="sm"
                className="h-8 gap-2 bg-primary text-xs hover:bg-primary/90"
                disabled={live && !canSend}
                onClick={live ? send : undefined}
              >
                {sending ? "Sending…" : "Send to customer"}
                <kbd className="rounded bg-white/15 px-1 font-mono text-[10px]">
                  ⌘↵
                </kbd>
              </Button>
              <button
                onClick={live ? suggestReply : undefined}
                disabled={drafting}
                className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-60"
              >
                <Sparkles
                  className={cn("h-3.5 w-3.5", drafting && "animate-pulse")}
                />
                {drafting ? "Writing a draft…" : "Suggest reply"}
              </button>
              <AttachButton
                files={attachments}
                onChange={setAttachments}
                disabled={sending}
              />
              <span className={cn(mono, "ml-auto text-gray-500")}>
                Draft kept per thread
              </span>
              <button
                onClick={() => {
                  setDraft(blankDraft());
                  setAttachments([]);
                  setForwarding(null);
                  setComposeOpen(false);
                }}
                className="rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
              >
                Discard
              </button>
            </div>
          </div>
        ) : (
          <div className="border border-amber-200 bg-amber-50/40">
            <div className="flex items-center gap-2 border-b border-amber-200 px-4 py-2">
              <MessageSquare className="h-3.5 w-3.5 text-amber-700" />
              <span className={cn(mono, "text-amber-800")}>Internal note</span>
              <span className={cn(mono, "ml-auto text-gray-500")}>
                only visible to staff
              </span>
              <button
                onClick={() => setComposeOpen(false)}
                className="rounded p-1 text-gray-400 hover:bg-amber-100 hover:text-gray-700"
                title="Collapse"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            <MentionTextarea
              value={note}
              onChange={setNote}
              staff={staff.filter((s) => s.id !== me.id)}
              textareaRef={noteRef}
              placeholder="Add an internal note · type @ to mention a colleague"
              className="w-full resize-none bg-transparent px-4 py-3 text-sm outline-none placeholder:text-gray-400"
            />
            <div className="flex items-center gap-2 border-t border-amber-200 px-4 py-2.5">
              <Button
                size="sm"
                className="h-8 gap-1.5 bg-primary text-xs hover:bg-primary/90"
                disabled={live && (sending || !note.trim())}
                onClick={live ? saveNote : undefined}
              >
                <Send className="h-3.5 w-3.5" /> Add note
              </Button>
              <button
                type="button"
                onClick={() => {
                  // Starts an @mention at the end; the picker opens as the
                  // name is typed.
                  const next = note && !/\s$/.test(note) ? `${note} @` : `${note}@`;
                  setNote(next);
                  requestAnimationFrame(() => {
                    noteRef.current?.focus();
                    noteRef.current?.setSelectionRange(next.length, next.length);
                  });
                }}
                className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
              >
                <AtSign className="h-3.5 w-3.5" /> Mention
              </button>
              <button
                onClick={() => setComposeOpen(false)}
                className="ml-auto rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
              >
                Discard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
